import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import RecommendedCard from '../components/RecommendedCard';
import MoviesModal from '../components/MovieModal';
import SeriesModal from '../components/SeriesModal';
import { fetchProviderDetails } from '../services/tmdb';

// ================== TRANSLATIONS ==================
const translations = {
  en: {
    ui: {
      contentPreferences: "Content Preferences",
      selectContentTypes: "Select Content Types",
      chooseTypes: "Choose the types of content you want to see in your daily recommendations",
      applyPreferences: "Apply Preferences",
      preferencesToggle: "Content Preferences",
      defaultLanguage: "Site Language",
      ratingFormat: "Rating Format",
      noChanges: "No changes made to your preferences.",
      preferenceSet: "Preferences set.",
      weWillAdd: "Preferences Updated.",
      streamingServices: "Streaming Services",
    },
    languagePrompt: {
      question: "Do you want to change the site language?",
      yes: "Yes",
      no: "No",
    },
    genreNames: {
      "HORROR THRILLERS": "HORROR THRILLERS",
      "MODERN COMEDY": "MODERN COMEDY",
      "ANIMATED ADVENTURES": "ANIMATED ADVENTURES",
      "ROMANTIC THRILLS": "ROMANTIC THRILLS",
      "PSYCHOLOGICAL THRILLERS": "PSYCHOLOGICAL THRILLERS",
      "DOPE SERIES": "DOPE SERIES",
      "DOCUMENTARY": "DOCUMENTARY",
    },
    genreTags: {
      "Horror": "Horror",
      "Comedy": "Comedy",
      "Animation": "Animation",
      "Romance": "Romance",
      "Thriller": "Thriller",
      "TV Series": "TV Series",
      "Documentary": "Documentary",
    },
    specialOccasionNames: {
      "CHRISTMAS SPECIALS": "CHRISTMAS SPECIALS",
    },
    specialOccasionTags: {
      "Christmas": "Christmas",
    },
  },
  fr: {
    ui: {
      contentPreferences: "Préférences de contenu",
      selectContentTypes: "Sélectionner les types de contenu",
      chooseTypes: "Choisissez les types de contenu que vous souhaitez voir dans vos recommandations quotidiennes",
      applyPreferences: "Appliquer les préférences",
      preferencesToggle: "Préférences de contenu",
      defaultLanguage: "Langue par défaut",
      ratingFormat: "Format de notation",
      noChanges: "Aucune modification apportée à vos préférences.",
      preferenceSet: "Préférences enregistrées.",
      weWillAdd: "Préférences mises à jour.",
      streamingServices: "Services de streaming",
    },
    languagePrompt: {
      question: "Stream4City is available in multiple languages!",
      yes: "Oui",
      no: "Non",
    },
    genreNames: {
      "HORROR THRILLERS": "THRILLERS D'HORREUR",
      "MODERN COMEDY": "COMÉDIE",
      "ANIMATED ADVENTURES": "AVENTURES ANIMÉES",
      "ROMANTIC THRILLS": "ROMANCE",
      "PSYCHOLOGICAL THRILLERS": "THRILLERS PSYCHOLOGIQUES",
      "DOPE SERIES": "SÉRIES GÉNIALES",
      "DOCUMENTARY": "DOCUMENTAIRE",
    },
    genreTags: {
      "Horror": "Horreur",
      "Comedy": "Comédie",
      "Animation": "Animation",
      "Romance": "Romance",
      "Thriller": "Thriller",
      "TV Series": "Séries TV",
      "Documentary": "Documentaire",
    },
    specialOccasionNames: {
      "CHRISTMAS SPECIALS": "SPÉCIAL NOËL",
    },
    specialOccasionTags: {
      "Christmas": "Noël",
    },
  },
  es: {
    ui: {
      contentPreferences: "Preferencias de contenido",
      selectContentTypes: "Seleccionar tipos de contenido",
      chooseTypes: "Elige los tipos de contenido que quieres ver en tus recomendaciones diarias",
      applyPreferences: "Aplicar preferencias",
      preferencesToggle: "Preferencias de contenido",
      defaultLanguage: "Idioma predeterminado",
      ratingFormat: "Formato de calificación",
      noChanges: "No se realizaron cambios en tus preferencias.",
      preferenceSet: "Preferencias establecidas.",
      weWillAdd: "Preferencias actualizadas.",
      streamingServices: "Servicios de streaming",
    },
    languagePrompt: {
      question: "¿Quieres cambiar el idioma del sitio?",
      yes: "Sí",
      no: "No",
    },
    genreNames: {
      "HORROR THRILLERS": "SUSPENSE TERRORÍFICO",
      "MODERN COMEDY": "COMEDIA",
      "ANIMATED ADVENTURES": "AVENTURAS ANIMADAS",
      "ROMANTIC THRILLS": "ROMANCE",
      "PSYCHOLOGICAL THRILLERS": "SUSPENSE PSICOLÓGICO",
      "DOPE SERIES": "SERIES INCREÍBLES",
      "DOCUMENTARY": "DOCUMENTAL",
    },
    genreTags: {
      "Horror": "Terror",
      "Comedy": "Comedia",
      "Animation": "Animación",
      "Romance": "Romance",
      "Thriller": "Suspense",
      "TV Series": "Series TV",
      "Documentary": "Documental",
    },
    specialOccasionNames: {
      "CHRISTMAS SPECIALS": "ESPECIAL NAVIDAD",
    },
    specialOccasionTags: {
      "Christmas": "Navidad",
    },
  },
  it: {
    ui: {
      contentPreferences: "Preferenze contenuti",
      selectContentTypes: "Seleziona tipi di contenuto",
      chooseTypes: "Scegli i tipi di contenuto che vuoi vedere nei tuoi consigli giornalieri",
      applyPreferences: "Applica preferenze",
      preferencesToggle: "Preferenze contenuti",
      defaultLanguage: "Lingua predefinita",
      ratingFormat: "Formato di valutazione",
      noChanges: "Nessuna modifica apportata alle tue preferenze.",
      preferenceSet: "Preferenze impostate.",
      weWillAdd: "Preferenze aggiornate.",
      streamingServices: "Servizi di streaming",
    },
    languagePrompt: {
      question: "Vuoi cambiare la lingua del sito?",
      yes: "Sì",
      no: "No",
    },
    genreNames: {
      "HORROR THRILLERS": "THRILLER HORROR",
      "MODERN COMEDY": "COMMEDIA",
      "ANIMATED ADVENTURES": "AVVENTURE ANIMATE",
      "ROMANTIC THRILLS": "ROMANCE",
      "PSYCHOLOGICAL THRILLERS": "THRILLER PSICOLOGICI",
      "DOPE SERIES": "SERIE STUPENDE",
      "DOCUMENTARY": "DOCUMENTARIO",
    },
    genreTags: {
      "Horror": "Orrore",
      "Comedy": "Commedia",
      "Animation": "Animazione",
      "Romance": "Romanzo",
      "Thriller": "Thriller",
      "TV Series": "Serie TV",
      "Documentary": "Documentario",
    },
    specialOccasionNames: {
      "CHRISTMAS SPECIALS": "SPECIALE NATALE",
    },
    specialOccasionTags: {
      "Christmas": "Natale",
    },
  },
  nl: {
    ui: {
      contentPreferences: "Inhoudsvoorkeuren",
      selectContentTypes: "Selecteer inhoudstypen",
      chooseTypes: "Kies de soorten inhoud die je wilt zien in je dagelijkse aanbevelingen",
      applyPreferences: "Voorkeuren toepassen",
      preferencesToggle: "Inhoudsvoorkeuren",
      defaultLanguage: "Standaardtaal",
      ratingFormat: "Beoordelingsformaat",
      noChanges: "Er zijn geen wijzigingen aangebracht in je voorkeuren.",
      preferenceSet: "Voorkeuren ingesteld.",
      weWillAdd: "Voorkeuren bijgewerkt.",
      streamingServices: "Streamingdiensten",
    },
    languagePrompt: {
      question: "Wil je de taal van de site wijzigen?",
      yes: "Ja",
      no: "Nee",
    },
    genreNames: {
      "HORROR THRILLERS": "HORRORTHRILLERS",
      "MODERN COMEDY": "KOMEDIE",
      "ANIMATED ADVENTURES": "GEANIMEERDE AVONTUREN",
      "ROMANTIC THRILLS": "ROMANTIEK",
      "PSYCHOLOGICAL THRILLERS": "PSYCHOLOGISCHE THRILLERS",
      "DOPE SERIES": "COOLE SERIES",
      "DOCUMENTARY": "DOCUMENTAIRE",
    },
    genreTags: {
      "Horror": "Horror",
      "Comedy": "Komedie",
      "Animation": "Animatie",
      "Romance": "Romantiek",
      "Thriller": "Thriller",
      "TV Series": "TV-series",
      "Documentary": "Documentaire",
    },
    specialOccasionNames: {
      "CHRISTMAS SPECIALS": "KERSTSPECIALS",
    },
    specialOccasionTags: {
      "Christmas": "Kerst",
    },
  },
};

// ============================================================
// ================ SPECIAL OCCASION ===============
// ============================================================
const specialOccasion = null;

// ============================================================
// ================ YOUR FULL HARDCODED DATA (enriched) ================
// ============================================================
const initialGenres = [
  {
    name: "HORROR THRILLERS",
    tag: "Horror",
    movies: [
      { id: 1008042, backdrop_path: '/7hEDrS3lbAEINmY9CY8eT4L8VBN.jpg', poster_path: '/kdPMUMJzyYAc4roD52qavX0nLIC.jpg', title: 'Talk To me', vote_average: 7.2, media_type: 'movie', year: '2022', extraInfo: '1h 35m' },
      { id: 1933, backdrop_path: '/6cNTPb6GdK2Qcevm5beGHjBHVqh.jpg', poster_path: '/p8g1vlTvpM6nr2hMMiZ1fUlKF0D.jpg', title: 'The Others', vote_average: 7.6, media_type: 'movie', year: '2001', extraInfo: '1h 44m' },
      { id: 14564, backdrop_path: '/78Gx5p1zVcbGIvDo88md4qtEIPf.jpg', poster_path: '/5FoczK6tDFzC9OyHiMMNIeNspek.jpg', title: 'Rings', vote_average: 5, media_type: 'movie', year: '2017', extraInfo: '1h 42m' },
      { id: 203834, backdrop_path: '/7FGrMT0ulCGEgsRDLZP28uew9pI.jpg', poster_path: '/oyszsO25OfSxkxhKCCnk9ZrAjkp.jpg', title: 'Jessabelle', vote_average: 5.4, media_type: 'movie', year: '2014', extraInfo: '1h 30m' },
      { id: 242512, backdrop_path: '/2Jw1A2mvdU8tBZ0yUlMfbhHvdFn.jpg', poster_path: '/f4qiYnnXZMUjI4OykZilWjxaciP.jpg', title: 'Ouija', vote_average: 5.1, media_type: 'movie', year: '2014', extraInfo: '1h 29m' },
      { id: 10065, backdrop_path: '/unE3qqgRlnAsoiiCx318Us8CwUC.jpg', poster_path: '/4D246dpe7yy2GvHI2IbpeqkUXry.jpg', title: 'The Amityville Horror', vote_average: 6.1, media_type: 'movie', year: '2005', extraInfo: '1h 30m' },
      { id: 301846, backdrop_path: '/4Ym1BRPiBAvesqnxHPvJUKHKmRg.jpg', poster_path: '/bSKyWFe1kWWr0Gf961DrM0PS0Jx.jpg', title: 'Howl', vote_average: 5.8, media_type: 'movie', year: '2015', extraInfo: '1h 32m' },
      { id: 1977, backdrop_path: '/23LfsCO7z7d3MPVbS0qUk2y1iJ6.jpg', poster_path: '/uEOWWRr8BbgPM0BiJiSTwHGaRx4.jpg', title: 'The Grudge 3', vote_average: 5.4, media_type: 'movie', year: '2009', extraInfo: '1h 30m' },
       { id: 134597, backdrop_path: '/idWKdSU8JPSjpsJCcdXkjdDiCSM.jpg', poster_path: '/qbhJiXi48fRQevHf56i2JmYqyJ7.jpg', title: 'The Collection', vote_average: 6.4, media_type: 'movie', year: '2012', extraInfo: '1h 22m' },
       { id: 1029281, backdrop_path: '/bziR8l6DHQAIWQDAy0Tdu4EgLYh.jpg', poster_path: '/kWNCRgt3ocv19bYO0sk7TRuZuFY.jpg', title: 'The Collection', vote_average: 6.9, media_type: 'movie', year: '2024', extraInfo: '1h 37m' },
       { id: 1029281, backdrop_path: '/bziR8l6DHQAIWQDAy0Tdu4EgLYh.jpg', poster_path: '/kWNCRgt3ocv19bYO0sk7TRuZuFY.jpg', title: 'The Collection', vote_average: 6.9, media_type: 'movie', year: '2024', extraInfo: '1h 37m' },


    ]
  },
  {
    name: "MODERN COMEDY",
    tag: "Comedy",
    movies: [
      { id: 12153, backdrop_path: '/eGlylHgbP6ZIVcuEKSKGYrxWDyN.jpg', poster_path: '/aHTUpo45qy9QYIOnVITGGqLoVcA.jpg', title: 'White Chicks', vote_average: 6.9, media_type: 'movie', year: '2004', extraInfo: '1h 49m' },
      { id: 4247, backdrop_path: '/Ak00qYJA7wINM0Z0mqxxhVEo0zW.jpg', poster_path: '/fVQFPRuw3yWXojYDJvA5EoFjUOY.jpg', title: 'Scary Movie', vote_average: 6.5, media_type: 'movie', year: '2000', extraInfo: '1h 28m' },
      { id: 854, backdrop_path: '/12DAHdiWTlips5oUbMSS4QI1lG9.jpg', poster_path: '/zJUvFtaGPQkcQYE8nmhU66ESpWe.jpg', title: 'The Mask', vote_average: 7, media_type: 'movie', year: '1994', extraInfo: '1h 41m' },
      { id: 9072, backdrop_path: '/AtZGyQMBPZ6YCjiM16mqekEOHG5.jpg', poster_path: '/gs6AnQy7ZfOCgz0wsl7yjQOxF6K.jpg', title: 'Little Man', vote_average: 5.9, media_type: 'movie', year: '2006', extraInfo: '1h 38m' },
      { id: 2109, backdrop_path: '/o6gRjgl3lnxvejd59oYZOXDYwAr.jpg', poster_path: '/we7wOLVFgxhzLzUt0qNe50xdIQZ.jpg', title: 'Rush Hour', vote_average: 7.1, media_type: 'movie', year: '1998', extraInfo: '1h 38m' },
      { id: 10771, backdrop_path: '/iNAtHqaS3SylV0wCkVr2IR6BClY.jpg', poster_path: '/2agrcNQRtgTsN5SbffC4H2BwYW2.jpg', title: 'The Tuxedo', vote_average: 5.7, media_type: 'movie', year: '2002', extraInfo: '1h 38m' },
      { id: 11090, backdrop_path: '/4twU4m15DD8uuqA8o6JyOjQS7TU.jpg', poster_path: '/9ofuojycOqt3TWamQ7vSMp6BgmO.jpg', title: 'The Animal', vote_average: 5.1, media_type: 'movie', year: '2001', extraInfo: '1h 24m' },
      { id: 193893, backdrop_path: '/pQc2ayo9Klk64DKsXKeVB3hN35Q.jpg', poster_path: '/w8pFMeEDIkDqU2eY3fmUP8dCg9R.jpg', title: 'Let*s Be Cops', vote_average: 6.5, media_type: 'movie', year: '2014', extraInfo: '1h 44m' },
      { id: 302699, backdrop_path: '/zqCPyAEvYgCObawlaB4vHrll4rZ.jpg', poster_path: '/hXQVMbRNpf7c93e8clMY4W5DRm6.jpg', title: 'Central Intelligence', vote_average: 6.5, media_type: 'movie', year: '2016', extraInfo: '1h 47m' },
      { id: 291870, backdrop_path: '/yfXYte5ASR8g4UYwhwFxKarbeqR.jpg', poster_path: '/qSJE2KloazcGrpGCCUVVX8ZKlsW.jpg', title: 'Dirty Grandpa', vote_average: 5.9, media_type: 'movie', year: '2016', extraInfo: '1h 42m' },
      { id: 64688, backdrop_path: '/1wi54CsirQ6TLUH88YcbkyBxxv2.jpg', poster_path: '/q0iOz3PuS2kLKBC1mSGNRVzNxu5.jpg', title: '21 Jump Street', vote_average: 7, media_type: 'movie', year: '2012', extraInfo: '1h 49m' },
      { id: 39513, backdrop_path: '/q9WEBrMlobyi1b8x4rMcasoyjJf.jpg', poster_path: '/y3fEPKUbpO1Yym1UyyPbwpp0XhO.jpg', title: 'Paul', vote_average: 6.7, media_type: 'movie', year: '2011', extraInfo: '1h 44m' },
      { id: 339846, backdrop_path: '/sdkaKzhF4ukTLcxjDoMhIzPbWQU.jpg', poster_path: '/6HE4xd8zloDqmjMZuhUCCw2UcY1.jpg', title: 'Baywatch', vote_average: 6.1, media_type: 'movie', year: '2017', extraInfo: '1h 56m' },
      { id: 445571, backdrop_path: '/gYSfiflvlGH3r6XD205vUWiJsJV.jpg', poster_path: '/85R8LMyn9f2Lev2YPBF8Nughrkv.jpg', title: 'Game Night', vote_average: 6.8, media_type: 'movie', year: '2018', extraInfo: '1h 40m' },
      { id: 433251, backdrop_path: '/1qfasIofxwD8DWOKPPphfNSOah6.jpg', poster_path: '/qXRmYSB9C6SHdxTgY3wB72Riz9Y.jpg', title: 'Naked', vote_average: 5.8, media_type: 'movie', year: '2017', extraInfo: '1h 36m' },
      { id: 667739, backdrop_path: '/5VD29GSKTRdK0GjdVju5qtiCrJ5.jpg', poster_path: '/uTCfTibqtk4f90cC59bLPMOmsfc.jpg', title: 'The Man From Toronto', vote_average: 6.4, media_type: 'movie', year: '2022', extraInfo: '1h 50m' },
    ]
  },
  {
    name: "ANIMATED ADVENTURES",
    tag: "Animation",
    movies: [
      { id: 585511, backdrop_path: '/8haCNaj3XIgQbBWr6XkOghE2iwR.jpg', poster_path: '/1HOYvwGFioUFL58UVvDRG6beEDm.jpg', title: 'Luck', vote_average: 7.8, media_type: 'movie', year: '2022', extraInfo: '1h 45m' },
      { id: 3933, backdrop_path: '/vfXOUsctUj7NdDNEiR35zt5Du0M.jpg', poster_path: '/isb2Qow76GpqYmsSyfdMfsYAjts.jpg', title: 'Corpse Bride', vote_average: 7.6, media_type: 'movie', year: '2005', extraInfo: '1h 17m' },
      { id: 587807, backdrop_path: '/cWlSnILiBXolR5vE1nHU2MzSxSp.jpg', poster_path: '/8XZI9QZ7Pm3fVkigWJPbrXCMzjq.jpg', title: 'Tom & Jerry', vote_average: 6.8, media_type: 'movie', year: '2021', extraInfo: '1h 41m' },
      { id: 315162, backdrop_path: '/lexPKf5OIcrVhFJkYKoOsnOZTWl.jpg', poster_path: '/kuf6dutpsT0vSVehic3EZIqkOBt.jpg', title: 'Puss in Boots: The Last Wish', vote_average: 8.2, media_type: 'movie', year: '2022', extraInfo: '1h 42m' },
      { id: 2062, backdrop_path: '/d2Dre7DZX2Z7ltz9XTCy2ElAQ8X.jpg', poster_path: '/1W8FTjVzNskmTJSfA9eufhyhtMB.jpg', title: 'Ratatouille', vote_average: 7.8, media_type: 'movie', year: '2007', extraInfo: '1h 51m' },
      { id: 508442, backdrop_path: '/7EslmAX1Pz0HGwpa8hnVAqiOWf9.jpg', poster_path: '/pEz5aROvfy5FBW1OTlrDO3VryWO.jpg', title: 'Soul', vote_average: 8.1, media_type: 'movie', year: '2020', extraInfo: '1h 40m' },
      { id: 9297, backdrop_path: '/ysiOFhfeSQgE7GWTfniB87Mh3a4.jpg', poster_path: '/puK9wM2dF9NE162wCn1tBgDwEw5.jpg', title: 'Monster House', vote_average: 6.7, media_type: 'movie', year: '2006', extraInfo: '1h 31m' },
      { id: 481084, backdrop_path: '/xS89uFXPKKwyWJgM7ljAmuAtDMh.jpg', poster_path: '/q1epO0eO8DWu8Vo8tPfvVlzW48T.jpg', title: 'The Addams Family', vote_average: 6.6, media_type: 'movie', year: '2019', extraInfo: '1h 26m' },
      { id: 277834, backdrop_path: '/i5TDR8gfLKjVENtibCcR4JI3Hfc.jpg', poster_path: '/9tzN8sPbyod2dsa0lwuvrwBDWra.jpg', title: 'Moana', vote_average: 7.6, media_type: 'movie', year: '2016', extraInfo: '1h 47m' },
      { id: 62177, backdrop_path: '/rrEL4NzClG4AEVHWpDrOLOeUmzb.jpg', poster_path: '/1XAuDtMWpL0sYSFK0R6EZate2Ux.jpg', title: 'Brave', vote_average: 7.0, media_type: 'movie', year: '2012', extraInfo: '1h 33m' },
      { id: 10340, backdrop_path: '/gkkho9nb4CiJ7qt8AJBy2gH0iSE.jpg', poster_path: '/340NcWz9SQXWQyf4oicMxjbrLOb.jpg', title: 'Lady and the Tramp', vote_average: 7.1, media_type: 'movie', year: '1955', extraInfo: '1h 16m' },
      { id: 1075794, backdrop_path: '/qFcP3mFk7kmPFESSI5tgSStlvrd.jpg', poster_path: '/gSOVog7ydsaF1YpgAqBqnKYFGY.jpg', title: 'Leo', vote_average: 7.4, media_type: 'movie', year: '2023', extraInfo: '1h 42m' },
      { id: 13053, backdrop_path: '/iQBucIw8yqKMwqGccch4eMd7cj1.jpg', poster_path: '/z9VHoUcZ1GiH3f3qYz7Me7Zc1Fd.jpg', title: 'Bolt', vote_average: 6.5, media_type: 'movie', year: '2008', extraInfo: '1h 36m' },
      { id: 328111, backdrop_path: '/A9wjsXPYYnffcI8SbxSPMeuWccq.jpg', poster_path: '/g3Hms6AE174doeGR1gz5zX5sVsv.jpg', title: 'The Secret Life of Pets', vote_average: 6.3, media_type: 'movie', year: '2016', extraInfo: '1h 27m' },
      { id: 1011985, backdrop_path: '/jBO0BcpfHPP79qZMxwYPVX8ozec.jpg', poster_path: '/nqXsAaQsKw2gKpkfhIgjXNDRqg7.jpg', title: 'Kung Fu Panda 4', vote_average: 7.0, media_type: 'movie', year: '2024', extraInfo: '1h 34m' },
      { id: 940551, backdrop_path: '/19OjK9QOe37cctpfnHr7CMgXL6A.jpg', poster_path: '/dpXxBDi0ut5svjh5fbAqLoaJSq8.jpg', title: 'Migration', vote_average: 7.4, media_type: 'movie', year: '2023', extraInfo: '1h 31m' },
      { id: 748783, backdrop_path: '/fxXXXouLIrqcAP3eHAA3nlRaywJ.jpg', poster_path: '/xYduFGuch9OwbCOEUiamml18ZoB.jpg', title: 'The Garfield Movie', vote_average: 7.0, media_type: 'movie', year: '2024', extraInfo: '1h 41m' },
      { id: 459003, backdrop_path: '/dHX04iFUxkBAfovy7nCoVAAEOWT.jpg', poster_path: '/eeJjd9JU2Mdj9d7nWRFLWlrcExi.jpg', title: 'Mavka: The Forest Song', vote_average: 7.2, media_type: 'movie', year: '2023', extraInfo: '1h 39m' },
      { id: 519182, backdrop_path: '/mFP1aLKpwo0Bkt7aD3FHcp537ft.jpg', poster_path: '/wWba3TaojhK7NdycRhoQpsG0FaH.jpg', title: 'Despicable Me 4', vote_average: 7.0, media_type: 'movie', year: '2024', extraInfo: '1h 34m' },
      { id: 269149, backdrop_path: '/nK073xC6t8L9DSb0PEFh45jUpHP.jpg', poster_path: '/9LRFNLthb0jfWC0HUDUWjYgpP9F.jpg', title: 'Zootopia', vote_average: 7.7, media_type: 'movie', year: '2016', extraInfo: '1h 48m' },
      { id: 1022796, backdrop_path: '/8EEttAmdvl2cWjTsCBJpNeevy1I.jpg', poster_path: '/vgJZSqKMXWDDx09iSIStGKfHMku.jpg', title: 'Wish', vote_average: 6.3, media_type: 'movie', year: '2023', extraInfo: '1h 35m' },
      { id: 808, backdrop_path: '/80BMzAGoSsYWnr9UGgqnNxdTUYD.jpg', poster_path: '/o04jZs5SXhbvhqO4981W7KJXOWZ.jpg', title: 'Shrek', vote_average: 7.8, media_type: 'movie', year: '2001', extraInfo: '1h 30m' },
      { id: 1022789, backdrop_path: '/hLuPF9HxtjbS7ZzSjMRG3RpUffp.jpg', poster_path: '/vpnVM9B6NMmQpWeZvzLvDESb2QY.jpg', title: 'Inside Out 2', vote_average: 7.6, media_type: 'movie', year: '2024', extraInfo: '1h 36m' },
      { id: 129, backdrop_path: '/aQDiKWHWeUhJq9jWkfC6iyuO7Uc.jpg', poster_path: '/39wmItIWsg5sZMyRUHLkWBcuVCM.jpg', title: 'Spirited Away', vote_average: 8.5, media_type: 'movie', year: '2001', extraInfo: '2h 5m' },
    ]
  },
  {
    name: "ROMANTIC THRILLS",
    tag: "Romance",
    movies: [
      { id: 537915, backdrop_path: '/cvZCK1rjrCj5auPReCHQDMSscMu.jpg', poster_path: '/u3B2YKUjWABcxXZ6Nm9h10hLUbh.jpg', title: 'After', vote_average: 7.1, media_type: 'movie', year: '2019', extraInfo: '1h 46m' },
      { id: 59965, backdrop_path: '/24ZTqOQDI2diAJzpg6YO7iHEdx9.jpg', poster_path: '/d0TIDrwnMFVjg2EO4LsXQn1mbbc.jpg', title: 'Abduction', vote_average: 5.9, media_type: 'movie', year: '2011', extraInfo: '1h 46m' },
      { id: 1016121, backdrop_path: '/gqr3yvnbGQM3uwe0CxtCZYbG7Lj.jpg', poster_path: '/ynjhFu3EIOsajGZYiomgIG6CLb0.jpg', title: 'Beautiful Disaster', vote_average: 6.7, media_type: 'movie', year: '2023', extraInfo: '1h 36m' },
      { id: 818647, backdrop_path: '/ancQp043jH5gN4nSeJ9anUcLob8.jpg', poster_path: '/jmkpZvMVIRrMFevxzOubSBfG0s0.jpg', title: 'Through My Window', vote_average: 7.4, media_type: 'movie', year: '2022', extraInfo: '1h 51m' },
      { id: 8966, backdrop_path: '/9sKL4RIqd6W96UvS5KoW94yQxUm.jpg', poster_path: '/3Gkb6jm6962ADUPaCBqzz9CTbn9.jpg', title: 'Twilight', vote_average: 6.3, media_type: 'movie', year: '2008', extraInfo: '2h 2m' },
      { id: 10096, backdrop_path: '/pZeqKRcVSoGA07lAb017h4hFeLF.jpg', poster_path: '/iNZdSIfhSCMtRILDNyhLn8UKeSG.jpg', title: '13 Going on 30', vote_average: 6.8, media_type: 'movie', year: '2004', extraInfo: '1h 38m' },
      { id: 50544, backdrop_path: '/mwB5I2QHVPoPwiAQyp8Ae9BNAeP.jpg', poster_path: '/hdHfiTfUWF70SIRjHHz5f6RAxI8.jpg', title: 'Friends with Benefits', vote_average: 6.7, media_type: 'movie', year: '2011', extraInfo: '1h 49m' },
      { id: 2105, backdrop_path: '/RCGnFHiNxEdAJlPMHlC9NwgibP.jpg', poster_path: '/5P68by2Thn8wHAziyWGEw2O7hco.jpg', title: 'American Pie ', vote_average: 6.6, media_type: 'movie', year: '1999', extraInfo: '1h 35m' },
      { id: 330483, backdrop_path: '/8n6VX153sTK5kNFWD1X0aF68ney.jpg', poster_path: '/6MF3cdAasnF17SG9bmeamQ066iJ.jpg', title: 'The Choice', vote_average: 5, media_type: 'movie', year: '2016', extraInfo: '1h 51m' },
      { id: 239571, backdrop_path: '/jUUHxT535m7Nry2S9QwyqB0mT8g.jpg', poster_path: '/twv17hergmeqXR2jVpKdv0JRPir.jpg', title: 'The Best of Me', vote_average: 7.5, media_type: 'movie', year: '2014', extraInfo: '1h 58m' },
      { id: 10229, backdrop_path: '/nbFbmPQivKDwNXZSv1UMSzpZcmU.jpg', poster_path: '/8lUYMvWdHA0Q0k5F76RQCeCBUkA.jpg', title: 'A Walk to Remember', vote_average: 7.8, media_type: 'movie', year: '2002', extraInfo: '1h 41m' },
      { id: 72570, backdrop_path: '/owAKFPjkXs0GQith6B9rF2VBsMd.jpg', poster_path: '/gcigtyy3U26UFnycedcKyi6hxSz.jpg', title: 'The Vow', vote_average: 7.2, media_type: 'movie', year: '2012', extraInfo: '1h 44m' },
      { id: 22971, backdrop_path: '/nAo2Wwn3bjRoZRcv0rM3g52vD2R.jpg', poster_path: '/guSvICQojnbrWKQCACI451ZS342.jpg', title: 'Dear Johm', vote_average: 6.8, media_type: 'movie', year: '2010', extraInfo: '1h 48m' },
      { id: 50646, backdrop_path: '/rpvVPfosf8AKl7qaJsp2YQSj9lH.jpg', poster_path: '/nAPSoAnYPjmZsdyrz0uVb6Z9KPJ.jpg', title: 'Crazy Stupid Love', vote_average: 7.3, media_type: 'movie', year: '2011', extraInfo: '1h 58m' },
      { id: 41630, backdrop_path: '/jZgcLtKYgQsJjPNEsgkkb3TDEnV.jpg', poster_path: '/9C09L1U9S13J4Ut029Qhu0oLcTQ.jpg', title: 'No Strings Attached', vote_average: 6.3, media_type: 'movie', year: '2011', extraInfo: '1h 48m' },
      { id: 603661, backdrop_path: '/wLHGLl6qSvSRuoWbVavQVZSfr5E.jpg', poster_path: '/prbZxJxGcy07y60eq8lCGMciTYz.jpg', title: 'The Hating Game', vote_average: 7.3, media_type: 'movie', year: '2021', extraInfo: '1h 42m' },
      { id: 9778, backdrop_path: '/7wVjkI8H604RcfWofRIM4ZTy63w.jpg', poster_path: '/alP7LHFEHgLDNxl1gb1QOXe9P9R.jpg', title: 'Serendipity', vote_average: 7.0, media_type: 'movie', year: '2001', extraInfo: '1h 30m' },
    ]
  },
  {
    name: "PSYCHOLOGICAL THRILLERS",
    tag: "Thriller",
    movies: [
      { id: 467632, backdrop_path: '/oSudh5LMW6UPrprfOWJvEq4cALx.jpg', poster_path: '/fOG5Cj9URclWa0zyESPBuRCvAFh.jpg', title: 'Bad Samaritan', vote_average: 6.7, media_type: 'movie', year: '2018', extraInfo: '1h 50m' },
      { id: 807356, backdrop_path: '/d9frK0clSHvKENcx3H9O41kJ6sj.jpg', poster_path: '/dXCjoI3jdtBrwPHgVsyNLkl8Rvs.jpg', title: 'Watcher', vote_average: 6.5, media_type: 'movie', year: '2022', extraInfo: '1h 31m' },
      { id: 526051, backdrop_path: '/q4fk7n5v6zhLX45UnhQD8fo7W45.jpg', poster_path: '/8WTEFOwFBx0mHurnEfv27I0FOe2.jpg', title: 'Look Away', vote_average: 6.5, media_type: 'movie', year: '2018', extraInfo: '1h 43m' },
      { id: 576845, backdrop_path: '/hszJEMq0oL0GMzz94H3bHU4Trk8.jpg', poster_path: '/n1ZRmjlk1BJTY7aASqACfPAaLn2.jpg', title: 'Last Night in Soho', vote_average: 7.3, media_type: 'movie', year: '2021', extraInfo: '1h 56m' },
      { id: 1140648, backdrop_path: '/wIqOnd9IOkNsi4lQsT29TUsARRS.jpg', poster_path: '/dGKQ4nPA5WDVbq2KdXkGpohEC7Y.jpg', title: 'Double Blind', vote_average: 6, media_type: 'movie', year: '2023', extraInfo: '1h 30m' },
      { id: 474764, backdrop_path: '/cdQ0Rowoe0lcriZH3kGXnH30fAO.jpg', poster_path: '/yake2myhbW7c6dKbmwYDy1i40bm.jpg', title: 'The Lodge', vote_average: 6.1, media_type: 'movie', year: '2019', extraInfo: '1h 48m' },
      { id: 814800, backdrop_path: '/x46cIkJBE0wDuOkTpL4p8VdGk1u.jpg', poster_path: '/oHhD5jD4S5ElPNNFCDKXJAzMZ5h.jpg', title: 'Goodnight Mommy', vote_average: 6.3, media_type: 'movie', year: '2014', extraInfo: '1h 39m' },
      { id: 14254, backdrop_path: '/ao6DovLyHbMoETCB7mXxFBMNRVW.jpg', poster_path: '/1xQlzKUDLuCe3SUGIGL6K1PtldH.jpg', title: 'The Uninvited', vote_average: 6.5, media_type: 'movie', year: '2009', extraInfo: '1h 27m' },
      { id: 83899, backdrop_path: '/7PbLX0R2Q5PWIpOaOp4RctApdwA.jpg', poster_path: '/l163jakfKvchUTVTtBi0KA7ZhIc.jpg', title: 'You*re Next', vote_average: 6.6, media_type: 'movie', year: '2011', extraInfo: '1h 35m' },
      { id: 664341, backdrop_path: '/zX7tjb8iKua2Tye6iiUU1IFssoR.jpg', poster_path: '/mlYBMEDysGlEcui19a00XMSs4V1.jpg', title: 'Eileen', vote_average: 6, media_type: 'movie', year: '2023', extraInfo: '1h 36m' },
      { id: 570670, backdrop_path: '/vUO7NYFH8wHpK9wnYUJzvuYFGUr.jpg', poster_path: '/5EufsDwXdY2CVttYOk2WtYhgKpa.jpg', title: 'The Invisible Man', vote_average: 7.1, media_type: 'movie', year: '2020', extraInfo: '2h 4m' },
      { id: 1137205, backdrop_path: '/jzU6aq1hm2ol9hSLIBlgmogTni1.jpg', poster_path: '/hgpUvybRQzRK6UagoHNQRrO8sUg.jpg', title: 'Last Straw', vote_average: 6, media_type: 'movie', year: '2023', extraInfo: '1h 30m' },
      { id: 210577, backdrop_path: '/c4tmGXVbtW9nsTnNPwj3nc4YHGo.jpg', poster_path: '/lv5xShBIDPe7m4ufdlV0IAc7Avk.jpg', title: 'Gone Girl', vote_average: 7.9, media_type: 'movie', year: '2014', extraInfo: '2h 29m' },
      { id: 833425, backdrop_path: '/yGCqqkjWS2A7GrKt6z1XvLSTVk9.jpg', poster_path: '/5cnLoWq9o5tuLe1Zq4BTX4LwZ2B.jpg', title: 'No Exit', vote_average: 6.9, media_type: 'movie', year: '2022', extraInfo: '1h 35m' },
    ]
  },
  {
    name: "DOPE SERIES",
    tag: "TV Series",
    series: [
      { id: 93405, backdrop_path: '/OvS2rb2kidlXGlxjc1mtHUGpYm.jpg', poster_path: '/fRKEvCCELSZwPN5jw3VqvRDnLDZ.jpg', title: 'Squid Game', vote_average: 8.3, media_type: 'tv', extraInfo: '2021-2025 • 2 Seasons' },
      { id: 1396, backdrop_path: '/hwIAR9FDsKULo8efoOUuxsFtO2u.jpg', poster_path: '/1XS1oqL89opfnbLl8WnZY1O1uJx.jpg', title: 'Breaking Bad', vote_average: 8.9, media_type: 'tv', extraInfo: '2008-2013 • 5 Seasons' },
      { id: 62560, backdrop_path: '/iQWfTcZgnkZPr55yTr0Q0iS2K9d.jpg', poster_path: '/kv1nRqgebSsREnd7vdC2pSGjpLo.jpg', title: 'Mr. Robot', vote_average: 8.3, media_type: 'tv', extraInfo: '2015-2019 • 4 Seasons' },
      { id: 78191, backdrop_path: '/kfWUEC7uEOtVrUU8vjpsabiR2e3.jpg', poster_path: '/yxIdKGEjagaLs5kMjw92kAHmopH.jpg', title: 'You', vote_average: 8.5, media_type: 'tv', extraInfo: '2018-2024 • 4 Seasons' },
      { id: 71446, backdrop_path: '/zAIippNnm6o0gYEtjapbjQSxP8G.jpg', poster_path: '/dmnwJGVergOFWCiLbPsQTyyzePw.jpg', title: 'Money Heist', vote_average: 8.2, media_type: 'tv', extraInfo: '2017-2021 • 5 Seasons' },
      { id: 218230, backdrop_path: '/4E5vzZiUxdE6ui3CvpdnsUvZIVX.jpg', poster_path: '/ecM5MY9g2mF6x5SrB2QWOYkcH98.jpg', title: "Death's Game", vote_average: 8.4, media_type: 'tv', extraInfo: '2023-2024 • 1 Season' },
    ]
  },
  {
    name: "DOCUMENTARY",
    tag: "Documentary",
    movies: [
      { id: 1229958, backdrop_path: '/of1GHvg6aZw7cW8T1PchJuEv50M.jpg', poster_path: '/f5uojWzwJTCHBIV3rmGPTmQG7TF.jpg', title: 'Lover, Stalker, Killer', vote_average: 6.9, media_type: 'movie', year: '2024', extraInfo: '1h 24m' },
      { id: 923632, backdrop_path: '/qs1NAo5ksRCAy20JHTFZivWAVlv.jpg', poster_path: '/iLUSFjdavIf0SrP7ldoQ1xomQVC.jpg', title: 'The Tinder Swindler', vote_average: 7, media_type: 'movie', year: '2022', extraInfo: '1h 54m' },
      { id: 259259, backdrop_path: '/uSu8llAwRPyoc2Cc4fKx3Bprgui.jpg', poster_path: '/s7D1qWOGSFyicpToIfXL0HRMCT7.jpg', title: 'Worst Ex Ever', vote_average: 7.6, media_type: 'tv', extraInfo: '2024 • 1 Season' },
      { id: 1147203, backdrop_path: '/xjsBe5KAZfxddDCTCVWzOhJWl1B.jpg', poster_path: '/7QGQWVvIRwP1tLaSzQoRUZVp7HA.jpg', title: 'Billion Dollar Heist', vote_average: 7.6, media_type: 'movie', year: '2023', extraInfo: '1h 27m' },
      { id: 1213997, backdrop_path: '/AtqkCiYm9ANpxEvhQFc50v5uT3k.jpg', poster_path: '/pdknUCJJtreQmMshFmy0ivBGmBK.jpg', title: 'Bitconned ', vote_average: 6.3, media_type: 'movie', year: '2023', extraInfo: '1h 33m' },
      { id: 253249, backdrop_path: '/948pYu8YEUHyPaR4LXtGa0m7oPR.jpg', poster_path: '/f4MOko7qeZ9Qxa4tDz3zYPIklQY.jpg', title: 'Ashley Madison: Sex, Lies & Scandal', vote_average: 6.6, media_type: 'tv', extraInfo: '2024 • 1 Season' },
      { id: 105177, backdrop_path: '/cqJZ1Zf6iIPOvpt2XlzsU6cyjqj.jpg', poster_path: '/xRAnPiN3H8Awud8R4TymthhB49o.jpg', title: 'Unsolved Mysteries', vote_average: 7.7, media_type: 'tv', extraInfo: '2020-2024 • 3 Seasons' },
      { id: 245551, backdrop_path: '/quZn1CECXHNHlcpr2HARIc1csd5.jpg', poster_path: '/2FGjlE4b3FfbTFBl0vahPsxQTfC.jpg', title: 'Can I Tell You a Secret?', vote_average: 4, media_type: 'tv', extraInfo: '2024 • 1 Season' },
      { id: 253384, backdrop_path: '/y9CA4hbCAb3bVkqQFlu2oJSXIK9.jpg', poster_path: '/ybuDMeRLa6qkoAtn6IhKQLJyEf.jpg', title: 'Dancing for the Devil: The 7M TikTok Cult', vote_average: 6.7, media_type: 'tv', extraInfo: '2024 • 1 Season' },
      { id: 272299, backdrop_path: '/w8bnbNFqkKhIfeYekNJaoI6e0w9.jpg', poster_path: '/crvYcZTN7TTEa85nClY3JqL2F0O.jpg', title: 'This Is the Zodiac Speaking', vote_average: 7.4, media_type: 'tv', extraInfo: '2024 • 1 Season' },
      { id: 272494, backdrop_path: '/8B8ps2F93roqgzK7xDts1Jl0E0a.jpg', poster_path: '/nzaoEzVNU6nOEigbbZzUXavduuX.jpg', title: 'The Manhattan Alien Abduction', vote_average: 6, media_type: 'tv', extraInfo: '2024 • 1 Season' },
    ]
  },
];

// ========== SUPPORTED GENRES ==========
const SUPPORTED_GENRES = [
  { id: 27,  name: "Horror", tag: "Horror" },
  { id: 35,  name: "Comedy", tag: "Comedy" },
  { id: 16,  name: "Animation", tag: "Animation" },
  { id: 10749, name: "Romance", tag: "Romance" },
  { id: 53,  name: "Thriller", tag: "Thriller" },
  { id: 99,  name: "Documentary", tag: "Documentary" }
];

const FETCHABLE_TAGS = new Set([...SUPPORTED_GENRES.map(g => g.tag), 'TV Series']);
const EXCLUDED_GENRES_ALL = [10767, 10768, 10764, 10752];
const API_KEY = '57240db50c2008e78c261e1a934627e4';
const RATE_LIMIT_WINDOW = 10000;
const RATE_LIMIT_MAX = 28;
const TWO_HOURS = 2 * 60 * 60 * 1000;

// ================== PROVIDER LIST ==================
const PROVIDER_IDS = [8, 337, 350, 43, 15, 1899, 528, 9, 99];

const PROVIDER_NAMES = {
  8: 'Netflix',
  337: 'Disney+',
  350: 'Apple TV+',
  43: 'Starz',
  15: 'Hulu',
  1899: 'HBO Max',
  528: 'AMC+',
  9: 'Amazon Prime Video',
  386: 'Peacock',
  283: 'Crunchyroll',
  99: 'Shudder',
};

const CACHE_KEY_PROVIDERS = 'cached_providers';

// ================== COMPONENT ==================
const RecommendedPage = () => {
  const getDefaultTags = () => {
    const defaultTags = SUPPORTED_GENRES.map(g => g.tag);
    defaultTags.push("TV Series");
    if (specialOccasion) {
      const specialOccasionKey = `specialOccasion_${specialOccasion.hiddenTag}`;
      if (!localStorage.getItem(specialOccasionKey)) {
        defaultTags.push(specialOccasion.tag);
      }
    }
    return defaultTags;
  };

  // --- State ---
  const [selectedContent, setSelectedContent] = useState(null);
  const [contentType, setContentType] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [currentIndexes, setCurrentIndexes] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const [showGenreFilter, setShowGenreFilter] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [detailsMap, setDetailsMap] = useState({});

  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    const savedLang = localStorage.getItem('preferredLanguage');
    return savedLang || 'en';
  });

  // ---- Rating format preference ----
  const [ratingFormat, setRatingFormat] = useState(() => {
    return localStorage.getItem('ratingFormat') || 'stars';
  });

  const [showLanguagePrompt, setShowLanguagePrompt] = useState(() => {
    const shown = localStorage.getItem('languagePromptShown') === 'true';
    return !shown;
  });
  const preferencesRef = useRef(null);
  const [blinkLanguage, setBlinkLanguage] = useState(false);

  const [selectedTags, setSelectedTags] = useState(() => {
    const savedTags = localStorage.getItem('selectedTags');
    if (savedTags) return JSON.parse(savedTags);
    return getDefaultTags();
  });
  const [preferenceMessage, setPreferenceMessage] = useState(null);

  const scrollPositionRef = useRef(0);

  // ===== Caching helpers =====
  const getCacheKey = (tag) => `cached_${tag}`;
  const getCachedItems = (tag) => {
    try {
      const data = localStorage.getItem(getCacheKey(tag));
      return data ? JSON.parse(data) : [];
    } catch { return []; }
  };
  const saveCachedItems = (tag, newItems) => {
    if (!newItems || newItems.length === 0) return;
    let existing = getCachedItems(tag);
    const newIds = new Set(newItems.map(item => `${item.media_type}-${item.id}`));
    existing = existing.filter(item => !newIds.has(`${item.media_type}-${item.id}`));
    const merged = [...existing, ...newItems];
    if (merged.length > 50) merged.splice(0, merged.length - 50);
    localStorage.setItem(getCacheKey(tag), JSON.stringify(merged));
  };
  const updateCachedItem = (tag, id, mediaType, updates) => {
    const cacheKey = getCacheKey(tag);
    let items = getCachedItems(tag);
    const index = items.findIndex(item => item.id === id && item.media_type === mediaType);
    if (index !== -1) {
      items[index] = { ...items[index], ...updates };
      localStorage.setItem(cacheKey, JSON.stringify(items));
    }
  };
  const shuffleArray = (arr) => {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  };

  // ===== Fetch details for items =====
  const fetchDetailsForItems = async (items, genreTag) => {
    const promises = items.map(async (item) => {
      const key = `${item.media_type}-${item.id}`;
      if (detailsMap[key]) return;
      try {
        let details;
        if (item.media_type === 'movie') {
          details = await fetchMovieDetails(item.id);
        } else if (item.media_type === 'tv') {
          details = await fetchSeriesDetails(item.id);
        }
        if (details) {
          setDetailsMap(prev => ({ ...prev, [key]: details }));
          let year = null;
          let extraInfo = null;
          if (item.media_type === 'movie') {
            const releaseDate = details.release_date;
            year = releaseDate ? releaseDate.slice(0,4) : null;
            const runtime = details.runtime;
            if (runtime) {
              const hours = Math.floor(runtime / 60);
              const minutes = runtime % 60;
              extraInfo = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
            }
          } else if (item.media_type === 'tv') {
            const firstAir = details.first_air_date;
            const lastAir = details.last_air_date;
            year = firstAir ? firstAir.slice(0,4) : null;
            let yearRange = '';
            if (firstAir && lastAir) {
              const firstYear = firstAir.slice(0,4);
              const lastYear = lastAir.slice(0,4);
              yearRange = firstYear === lastYear ? firstYear : `${firstYear}-${lastYear}`;
            } else if (firstAir) {
              yearRange = firstAir.slice(0,4);
            } else {
              yearRange = 'N/A';
            }
            const seasons = details.number_of_seasons;
            let seasonText = '';
            if (seasons) {
              seasonText = seasons === 1 ? '1 Season' : `${seasons} Seasons`;
            } else {
              seasonText = 'N/A';
            }
            extraInfo = `${yearRange} • ${seasonText}`;
          }
          if (genreTag && (year || extraInfo)) {
            updateCachedItem(genreTag, item.id, item.media_type, { year, extraInfo });
          }
        }
      } catch (e) {
        console.warn(`Failed to fetch details for ${item.media_type} ${item.id}`);
      }
    });
    await Promise.allSettled(promises);
  };

  // ===== Build initial genreData =====
  const buildInitialGenreData = useCallback(async () => {
    const currentTags = JSON.parse(localStorage.getItem('selectedTags')) || getDefaultTags();
    const allGenres = [];
    if (specialOccasion) {
      const specialGenre = { ...specialOccasion, isSpecial: true, movies: specialOccasion.movies || [], series: specialOccasion.series || [] };
      if (currentTags.includes(specialOccasion.tag)) allGenres.push(specialGenre);
    }
    initialGenres.forEach(g => {
      if (currentTags.includes(g.tag)) {
        const cached = getCachedItems(g.tag);
        let combined = [];
        if (g.movies) combined = combined.concat(g.movies.map(item => ({ ...item, isHardcoded: true })));
        if (g.series) combined = combined.concat(g.series.map(item => ({ ...item, isHardcoded: true })));
        const cachedItems = cached.map(item => ({ ...item, isHardcoded: false }));
        const seen = new Set();
        const unique = [];
        const allItems = [...cachedItems, ...combined];
        allItems.forEach(item => {
          const key = `${item.media_type}-${item.id}`;
          if (!seen.has(key)) {
            seen.add(key);
            unique.push(item);
          } else {
            const existingIdx = unique.findIndex(u => `${u.media_type}-${u.id}` === key);
            if (existingIdx !== -1 && item.isHardcoded) {
              unique[existingIdx] = item;
            }
          }
        });
        const shuffled = shuffleArray(unique);
        const selected = shuffled.slice(0, 10);
        const newGenre = { ...g };
        if (g.movies) newGenre.movies = selected;
        if (g.series) newGenre.series = selected;
        allGenres.push(newGenre);
      }
    });
    setInitialLoading(false);
    return allGenres;
  }, []);

  // ===== State initialization =====
  const [genreData, setGenreData] = useState([]);
  const [extraMovies, setExtraMovies] = useState({});
  const [totalPagesCache, setTotalPagesCache] = useState({});
  const [hasFetched, setHasFetched] = useState({});
  const [loadingGenres, setLoadingGenres] = useState({});
  const [backdropLoading, setBackdropLoading] = useState({});
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const requestTimestampsRef = useRef([]);
  const backdropQueueRef = useRef([]);
  const backdropFetchingRef = useRef({});
  const backdropCacheRef = useRef({});
  const [backdropMap, setBackdropMap] = useState({});
  const fetchedPagesRef = useRef({});
  const wrapperRefs = useRef({});
  const rowRefs = useRef({});
  const [cardWidths, setCardWidths] = useState({});

  // ===== Provider data state =====
  const [providerData, setProviderData] = useState([]);
  const [providerLoading, setProviderLoading] = useState(true);
  const [providerIndex, setProviderIndex] = useState(0);
  const providerWrapperRef = useRef(null);
  const providerRowRef = useRef(null);

  const navigate = useNavigate();

  // ===== Fetch provider logos (cached permanently) =====
  useEffect(() => {
    // Preload all provider PNGs
    PROVIDER_IDS.forEach(id => {
      const img = new Image();
      img.src = `/providers/${id}.png`;
    });

    const fetchProviders = async () => {
      // Check cache first
      const cached = localStorage.getItem(CACHE_KEY_PROVIDERS);
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          if (Array.isArray(parsed) && parsed.length > 0) {
            setProviderData(parsed);
            setProviderLoading(false);
            return;
          }
        } catch (e) {
          // ignore parsing error, refetch
        }
      }

      // No valid cache – fetch from API
      setProviderLoading(true);
      const results = [];
      for (const id of PROVIDER_IDS) {
        try {
          const data = await fetchProviderDetails(id);
          if (data && data.logo_path) {
            results.push({
              id: id,
              logo: `https://image.tmdb.org/t/p/w92${data.logo_path}`,
              name: PROVIDER_NAMES[id] || id.toString(),
            });
          } else {
            results.push({
              id: id,
              logo: null,
              name: PROVIDER_NAMES[id] || id.toString(),
            });
          }
        } catch (e) {
          results.push({
            id: id,
            logo: null,
            name: PROVIDER_NAMES[id] || id.toString(),
          });
        }
      }
      setProviderData(results);
      // Store in cache permanently (no expiry)
      localStorage.setItem(CACHE_KEY_PROVIDERS, JSON.stringify(results));
      setProviderLoading(false);
    };
    fetchProviders();
  }, []);

  useEffect(() => {
    const load = async () => {
      const data = await buildInitialGenreData();
      setGenreData(data);
      const allItems = [];
      data.forEach(genre => {
        const items = genre.movies || genre.series || [];
        items.forEach(item => {
          if (!item.year && !item.extraInfo) {
            allItems.push(item);
          }
        });
      });
      if (allItems.length > 0) {
        const batchSize = 5;
        for (let i = 0; i < allItems.length; i += batchSize) {
          const batch = allItems.slice(i, i + batchSize);
          await fetchDetailsForItems(batch);
          await new Promise(r => setTimeout(r, 200));
        }
      }
      data.forEach(genre => {
        const tag = genre.tag;
        const items = genre.movies || genre.series || [];
        items.forEach(item => {
          if (!item.isHardcoded && !item.english_backdrop_url && !item.backdrop_path) {
            queueBackdropFetch(item.id, item.media_type, tag);
          }
        });
      });
    };
    load();
  }, []);

  useEffect(() => {
    localStorage.removeItem('recommendedIndexes');
    setCurrentIndexes({});
    setExtraMovies({});
    setTotalPagesCache({});
    setHasFetched({});
    setLoadingGenres({});
    setBackdropLoading({});
    setBackdropMap({});
    setCooldownSeconds(0);
    requestTimestampsRef.current = [];
    backdropQueueRef.current = [];
    backdropFetchingRef.current = {};
    backdropCacheRef.current = {};
    fetchedPagesRef.current = {};
  }, []);

  const preloadBaseImages = (items) => {
    if (!items || !items.length) return;
    const isMobile = windowWidth < 769;
    items.forEach(item => {
      const imagePath = isMobile ? item.poster_path : (item.backdrop_path || item.poster_path);
      if (!imagePath) return;
      const baseUrl = isMobile ? `https://image.tmdb.org/t/p/w342${imagePath}` : `https://image.tmdb.org/t/p/w780${imagePath}`;
      const img = new Image();
      img.src = baseUrl;
    });
  };

  useEffect(() => {
    genreData.forEach(genre => {
      const items = genre.movies || genre.series || [];
      preloadBaseImages(items);
    });
  }, [genreData, windowWidth]);

  // ===== Rate Limit Helpers =====
  const canMakeRequests = (numRequests) => {
    const now = Date.now();
    const windowStart = now - RATE_LIMIT_WINDOW;
    const recent = requestTimestampsRef.current.filter(t => t > windowStart);
    const total = recent.length + numRequests;
    return total <= RATE_LIMIT_MAX;
  };
  const recordRequests = (numRequests) => {
    const now = Date.now();
    for (let i = 0; i < numRequests; i++) {
      requestTimestampsRef.current.push(now);
    }
    const windowStart = now - RATE_LIMIT_WINDOW;
    requestTimestampsRef.current = requestTimestampsRef.current.filter(t => t > windowStart);
  };
  const getCooldownSeconds = useCallback((numRequests = 1) => {
    const now = Date.now();
    const windowStart = now - RATE_LIMIT_WINDOW;
    const recent = requestTimestampsRef.current.filter(t => t > windowStart).sort((a, b) => a - b);
    if (recent.length === 0) return 0;
    const neededSlots = RATE_LIMIT_MAX - numRequests;
    const excess = recent.length - neededSlots;
    if (excess <= 0) return 0;
    const targetIndex = excess - 1;
    const targetTimestamp = recent[targetIndex];
    const waitMs = targetTimestamp + RATE_LIMIT_WINDOW - now;
    return Math.max(0, Math.ceil(waitMs / 1000));
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCooldownSeconds(getCooldownSeconds(2));
    }, 500);
    return () => clearInterval(interval);
  }, [getCooldownSeconds]);

  // ===== Fetch Movie Genre =====
  const fetchGenreMovies = async (tag, skipTotalPages = false) => {
    const genre = SUPPORTED_GENRES.find(g => g.tag === tag);
    if (!genre) return [];
    const requestsNeeded = skipTotalPages ? 1 : 2;
    await new Promise((resolve) => {
      const check = () => {
        if (canMakeRequests(requestsNeeded)) resolve();
        else setTimeout(check, 500);
      };
      check();
    });
    setLoadingGenres(prev => ({ ...prev, [tag]: true }));
    try {
      const baseUrl = 'https://api.themoviedb.org/3/discover/movie';
      const params = new URLSearchParams({
        api_key: API_KEY,
        with_genres: genre.id,
        'vote_count.gte': 500,
        with_original_language: 'en',
        'primary_release_date.gte': '2006-01-01',
        language: 'en-US',
        include_adult: false,
      });
      let excludeGenres = [...EXCLUDED_GENRES_ALL];
      if (tag !== 'Animation') excludeGenres.push(16);
      if (tag === 'Horror') excludeGenres.push(35);
      if (excludeGenres.length) params.append('without_genres', excludeGenres.join(','));
      if (!fetchedPagesRef.current[tag]) fetchedPagesRef.current[tag] = new Set();
      let totalPages = totalPagesCache[tag];
      if (!skipTotalPages || totalPages === undefined) {
        const response = await fetch(`${baseUrl}?${params.toString()}`);
        if (!response.ok) throw new Error(`Failed to fetch total pages for ${tag}`);
        const data = await response.json();
        totalPages = data.total_pages;
        if (totalPages < 1) totalPages = 1;
        setTotalPagesCache(prev => ({ ...prev, [tag]: totalPages }));
        recordRequests(1);
        const availablePages = totalPages - fetchedPagesRef.current[tag].size;
        if (availablePages <= 0) {
          setHasFetched(prev => ({ ...prev, [tag]: true }));
          return [];
        }
        let randomPage;
        do { randomPage = Math.floor(Math.random() * totalPages) + 1; }
        while (fetchedPagesRef.current[tag].has(randomPage));
        fetchedPagesRef.current[tag].add(randomPage);
        const pageParams = new URLSearchParams(params);
        pageParams.set('page', randomPage);
        const pageResponse = await fetch(`${baseUrl}?${pageParams.toString()}`);
        if (!pageResponse.ok) throw new Error(`Failed to fetch random page for ${tag}`);
        const pageData = await pageResponse.json();
        recordRequests(1);
        const movies = pageData.results.filter(m => m.poster_path).map(m => ({ ...m, media_type: 'movie' }));
        setHasFetched(prev => ({ ...prev, [tag]: true }));
        return movies;
      } else {
        const availablePages = totalPages - fetchedPagesRef.current[tag].size;
        if (availablePages <= 0) return [];
        let randomPage;
        do { randomPage = Math.floor(Math.random() * totalPages) + 1; }
        while (fetchedPagesRef.current[tag].has(randomPage));
        fetchedPagesRef.current[tag].add(randomPage);
        const pageParams = new URLSearchParams(params);
        pageParams.set('page', randomPage);
        const pageResponse = await fetch(`${baseUrl}?${pageParams.toString()}`);
        if (!pageResponse.ok) throw new Error(`Failed to fetch random page for ${tag}`);
        const pageData = await pageResponse.json();
        recordRequests(1);
        const movies = pageData.results.filter(m => m.poster_path).map(m => ({ ...m, media_type: 'movie' }));
        setHasFetched(prev => ({ ...prev, [tag]: true }));
        return movies;
      }
    } catch (error) {
      console.error(`Error fetching ${tag}:`, error);
      return [];
    } finally {
      setLoadingGenres(prev => ({ ...prev, [tag]: false }));
    }
  };

  // ===== Fetch TV Series =====
  const fetchTVSeries = async (tag = 'TV Series', skipTotalPages = false) => {
    const requestsNeeded = skipTotalPages ? 1 : 2;
    await new Promise((resolve) => {
      const check = () => {
        if (canMakeRequests(requestsNeeded)) resolve();
        else setTimeout(check, 500);
      };
      check();
    });
    setLoadingGenres(prev => ({ ...prev, [tag]: true }));
    try {
      const baseUrl = 'https://api.themoviedb.org/3/discover/tv';
      const params = new URLSearchParams({
        api_key: API_KEY,
        'vote_count.gte': 2000,
        with_original_language: 'en',
        'first_air_date.gte': '2006-01-01',
        language: 'en-US',
        include_adult: false,
      });
      const excludeGenres = [10763, 10767, 10768, 10764, 10402, 16, 10765];
      params.append('without_genres', excludeGenres.join(','));
      if (!fetchedPagesRef.current[tag]) fetchedPagesRef.current[tag] = new Set();
      let totalPages = totalPagesCache[tag];
      if (!skipTotalPages || totalPages === undefined) {
        const response = await fetch(`${baseUrl}?${params.toString()}`);
        if (!response.ok) throw new Error(`Failed to fetch total pages for ${tag}`);
        const data = await response.json();
        totalPages = data.total_pages;
        if (totalPages < 1) totalPages = 1;
        setTotalPagesCache(prev => ({ ...prev, [tag]: totalPages }));
        recordRequests(1);
        const availablePages = totalPages - fetchedPagesRef.current[tag].size;
        if (availablePages <= 0) {
          setHasFetched(prev => ({ ...prev, [tag]: true }));
          return [];
        }
        let randomPage;
        do { randomPage = Math.floor(Math.random() * totalPages) + 1; }
        while (fetchedPagesRef.current[tag].has(randomPage));
        fetchedPagesRef.current[tag].add(randomPage);
        const pageParams = new URLSearchParams(params);
        pageParams.set('page', randomPage);
        const pageResponse = await fetch(`${baseUrl}?${pageParams.toString()}`);
        if (!pageResponse.ok) throw new Error(`Failed to fetch random page for ${tag}`);
        const pageData = await pageResponse.json();
        recordRequests(1);
        const series = pageData.results.filter(s => s.poster_path).map(s => ({ ...s, media_type: 'tv' }));
        setHasFetched(prev => ({ ...prev, [tag]: true }));
        return series;
      } else {
        const availablePages = totalPages - fetchedPagesRef.current[tag].size;
        if (availablePages <= 0) return [];
        let randomPage;
        do { randomPage = Math.floor(Math.random() * totalPages) + 1; }
        while (fetchedPagesRef.current[tag].has(randomPage));
        fetchedPagesRef.current[tag].add(randomPage);
        const pageParams = new URLSearchParams(params);
        pageParams.set('page', randomPage);
        const pageResponse = await fetch(`${baseUrl}?${pageParams.toString()}`);
        if (!pageResponse.ok) throw new Error(`Failed to fetch random page for ${tag}`);
        const pageData = await pageResponse.json();
        recordRequests(1);
        const series = pageData.results.filter(s => s.poster_path).map(s => ({ ...s, media_type: 'tv' }));
        setHasFetched(prev => ({ ...prev, [tag]: true }));
        return series;
      }
    } catch (error) {
      console.error(`Error fetching ${tag}:`, error);
      return [];
    } finally {
      setLoadingGenres(prev => ({ ...prev, [tag]: false }));
    }
  };

  // ===== Fetch English backdrop =====
  const fetchEnglishBackdrop = async (id, type = 'movie') => {
    const cacheKey = `${type}-${id}`;
    if (backdropCacheRef.current[cacheKey]) return backdropCacheRef.current[cacheKey];
    if (backdropFetchingRef.current[cacheKey]) return;
    backdropFetchingRef.current[cacheKey] = true;
    try {
      const baseUrl = type === 'tv' ? `https://api.themoviedb.org/3/tv/${id}/images` : `https://api.themoviedb.org/3/movie/${id}/images`;
      const url = `${baseUrl}?api_key=${API_KEY}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch images');
      const data = await response.json();
      const validBackdrops = (data.backdrops || []).filter(b => b.file_path);
      const englishBackdrop = validBackdrops.find(b => b.iso_639_1 === 'en');
      const fallbackBackdrop = validBackdrops[0];
      const backdrop = englishBackdrop || fallbackBackdrop;
      if (backdrop) {
        const meta = {
          url: `https://image.tmdb.org/t/p/w780${backdrop.file_path}`,
          isEnglish: !!englishBackdrop,
        };
        backdropCacheRef.current[cacheKey] = meta;
        return meta;
      }
      return null;
    } catch (error) {
      console.error(`Error fetching backdrop for ${type} ${id}:`, error);
      return null;
    } finally {
      backdropFetchingRef.current[cacheKey] = false;
    }
  };

  // ===== Backdrop queue =====
  const processBackdropQueue = useCallback(async () => {
    while (backdropQueueRef.current.length > 0) {
      if (!canMakeRequests(1)) {
        const now = Date.now();
        const windowStart = now - RATE_LIMIT_WINDOW;
        const oldest = requestTimestampsRef.current.filter(t => t > windowStart).sort((a,b) => a-b)[0];
        const waitTime = oldest ? (windowStart + RATE_LIMIT_WINDOW - now) : 0;
        if (waitTime > 0) await new Promise(resolve => setTimeout(resolve, waitTime + 100));
        continue;
      }
      const { id, type, genreTag } = backdropQueueRef.current.shift();
      const cacheKey = `${type}-${id}`;
      if (backdropCacheRef.current[cacheKey]) continue;
      const meta = await fetchEnglishBackdrop(id, type);
      if (meta) {
        setBackdropMap(prev => ({ ...prev, [cacheKey]: meta }));
        if (genreTag) {
          const filePath = meta.url.replace('https://image.tmdb.org/t/p/w780', '');
          updateCachedItem(genreTag, id, type, {
            backdrop_path: filePath,
            isEnglish: meta.isEnglish,
            english_backdrop_url: meta.isEnglish ? meta.url : null
          });
        }
      }
      recordRequests(1);
      await new Promise(resolve => setTimeout(resolve, 200));
    }
  }, []);

  const queueBackdropFetch = (id, type, genreTag) => {
    const cacheKey = `${type}-${id}`;
    if (backdropCacheRef.current[cacheKey]) return;
    if (backdropFetchingRef.current[cacheKey]) return;
    if (backdropQueueRef.current.some(item => item.id === id && item.type === type)) return;
    backdropQueueRef.current.push({ id, type, genreTag });
    processBackdropQueue();
  };

  // ===== Load more =====
  const handleLoadMore = async (genreTag) => {
    if (loadingGenres[genreTag] || backdropLoading[genreTag]) return;
    const extras = extraMovies[genreTag] || [];
    if (extras.length > 0) {
      setLoadingGenres(prev => ({ ...prev, [genreTag]: true }));
      await new Promise(resolve => setTimeout(resolve, 500));
      const toAdd = extras.slice(0, 10);
      const remaining = extras.slice(10);
      const itemsForCache = toAdd.map(item => ({
        ...item,
        backdrop_path: null,
        english_backdrop_url: null,
        isEnglish: false,
      }));
      saveCachedItems(genreTag, itemsForCache);
      preloadBaseImages(toAdd);
      toAdd.forEach(item => {
        queueBackdropFetch(item.id, item.media_type, genreTag);
      });
      await fetchDetailsForItems(toAdd, genreTag);
      setGenreData(prev => prev.map(g => {
        if (g.tag !== genreTag) return g;
        if (g.movies) return { ...g, movies: [...g.movies, ...toAdd] };
        if (g.series) return { ...g, series: [...g.series, ...toAdd] };
        return g;
      }));
      setExtraMovies(prev => ({ ...prev, [genreTag]: remaining }));
      setLoadingGenres(prev => ({ ...prev, [genreTag]: false }));
      return;
    }
    if (!FETCHABLE_TAGS.has(genreTag)) return;
    const skipTotalPages = hasFetched[genreTag] && totalPagesCache[genreTag] !== undefined;
    const requestsNeeded = skipTotalPages ? 1 : 2;
    if (!canMakeRequests(requestsNeeded)) {
      setCooldownSeconds(getCooldownSeconds(requestsNeeded));
      return;
    }
    setLoadingGenres(prev => ({ ...prev, [genreTag]: true }));
    setBackdropLoading(prev => ({ ...prev, [genreTag]: true }));
    let fetched;
    if (genreTag === 'TV Series') fetched = await fetchTVSeries('TV Series', skipTotalPages);
    else fetched = await fetchGenreMovies(genreTag, skipTotalPages);
    setLoadingGenres(prev => ({ ...prev, [genreTag]: false }));
    if (fetched.length === 0) {
      setBackdropLoading(prev => ({ ...prev, [genreTag]: false }));
      return;
    }
    const firstBatch = fetched.slice(0, 10);
    const secondBatch = fetched.slice(10, 20);
    const itemsForCache = firstBatch.map(item => ({
      ...item,
      backdrop_path: null,
      english_backdrop_url: null,
      isEnglish: false,
    }));
    saveCachedItems(genreTag, itemsForCache);
    preloadBaseImages(firstBatch);
    preloadBaseImages(secondBatch);
    firstBatch.forEach(item => {
      queueBackdropFetch(item.id, item.media_type, genreTag);
    });
    await fetchDetailsForItems(firstBatch, genreTag);
    setGenreData(prev => prev.map(g => {
      if (g.tag !== genreTag) return g;
      if (g.movies) return { ...g, movies: [...g.movies, ...firstBatch] };
      if (g.series) return { ...g, series: [...g.series, ...firstBatch] };
      return g;
    }));
    setExtraMovies(prev => ({ ...prev, [genreTag]: secondBatch }));
    setBackdropLoading(prev => ({ ...prev, [genreTag]: false }));
  };

  // ===== Translation helpers =====
  const getTranslation = (key, ...args) => {
    const langData = translations[selectedLanguage] || translations.en;
    const keys = key.split('.');
    let value = langData;
    for (const k of keys) {
      if (value && value[k] !== undefined) value = value[k];
      else {
        const enData = translations.en;
        let fallback = enData;
        for (const fk of keys) {
          if (fallback && fallback[fk] !== undefined) fallback = fallback[fk];
          else return key;
        }
        if (typeof fallback === 'function') return fallback(...args);
        return fallback;
      }
    }
    if (typeof value === 'function') return value(...args);
    return value;
  };

  const getTranslatedGenreName = (originalName) => translations[selectedLanguage]?.genreNames?.[originalName] || originalName;
  const getTranslatedGenreTag = (originalTag) => translations[selectedLanguage]?.genreTags?.[originalTag] || originalTag;
  const getTranslatedSpecialName = (originalName) => originalName ? (translations[selectedLanguage]?.specialOccasionNames?.[originalName] || originalName) : '';
  const getTranslatedSpecialTag = (originalTag) => originalTag ? (translations[selectedLanguage]?.specialOccasionTags?.[originalTag] || originalTag) : '';

  const toggleTag = (tag) => setSelectedTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);

  const resetToDefault = () => {
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('recommended_') || ['selectedTags','preferredLanguage','ratingFormat','languagePromptShown','recommendedPageVisits','recommendedSelection'].includes(key)) {
        localStorage.removeItem(key);
      }
    });
    window.location.reload();
  };

  const applyGenreFilter = () => {
    const previousTags = JSON.parse(localStorage.getItem('selectedTags') || '[]');
    const addedTags = selectedTags.filter(tag => !previousTags.includes(tag));
    const removedTags = previousTags.filter(tag => !selectedTags.includes(tag));
    const message = getTranslation('ui.weWillAdd', addedTags, removedTags);
    localStorage.setItem('preferredLanguage', selectedLanguage);
    localStorage.setItem('selectedTags', JSON.stringify(selectedTags));
    localStorage.setItem('ratingFormat', ratingFormat);
    localStorage.setItem('preferenceMessage', message);
    localStorage.removeItem('recommendedSelection');
    localStorage.removeItem('recommendedIndexes');
    window.scrollTo(0, 0);
    window.location.reload();
  };

  const handleLanguagePromptNo = () => { localStorage.setItem('languagePromptShown', 'true'); setShowLanguagePrompt(false); };
  const handleLanguagePromptYes = () => {
    localStorage.setItem('languagePromptShown', 'true');
    setShowLanguagePrompt(false);
    setShowGenreFilter(true);
    setTimeout(() => {
      preferencesRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setBlinkLanguage(true);
      setTimeout(() => setBlinkLanguage(false), 5000);
    }, 300);
  };

  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => {
    const message = localStorage.getItem('preferenceMessage');
    if (message) {
      setPreferenceMessage(message);
      localStorage.removeItem('preferenceMessage');
      setTimeout(() => setPreferenceMessage(null), 5000);
    }
  }, []);
  useEffect(() => {
    if (showGenreFilter) document.querySelector('.preferences-container')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [showGenreFilter]);
  useEffect(() => {
    const saved = localStorage.getItem('recommendedIndexes');
    if (saved) setCurrentIndexes(JSON.parse(saved));
    else {
      const initial = {};
      genreData.forEach(g => initial[g.name] = 0);
      setCurrentIndexes(initial);
      localStorage.setItem('recommendedIndexes', JSON.stringify(initial));
    }
  }, [genreData]);
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  useEffect(() => {
    if (Object.keys(currentIndexes).length > 0) localStorage.setItem('recommendedIndexes', JSON.stringify(currentIndexes));
  }, [currentIndexes]);
  useEffect(() => () => {
    const reset = {};
    Object.keys(currentIndexes).forEach(key => reset[key] = 0);
    localStorage.setItem('recommendedIndexes', JSON.stringify(reset));
    fetchedPagesRef.current = {};
  }, []);

  useLayoutEffect(() => {
    const measure = () => {
      const newCardWidths = {};
      Object.keys(wrapperRefs.current).forEach(genreName => {
        const wrapper = wrapperRefs.current[genreName];
        const row = rowRefs.current[genreName];
        if (!wrapper || !row || row.children.length === 0) return;
        const firstCard = row.children[0];
        const rect = firstCard.getBoundingClientRect();
        const cardWidth = rect.width;
        if (cardWidth > 0) newCardWidths[genreName] = cardWidth;
      });
      setCardWidths(prev => ({ ...prev, ...newCardWidths }));
    };
    const timeoutId = setTimeout(measure, 50);
    window.addEventListener('resize', measure);
    return () => { clearTimeout(timeoutId); window.removeEventListener('resize', measure); };
  }, [genreData, windowWidth]);

  const fetchMovieDetails = async (movieId) => {
    try {
      const res = await fetch(`https://api.themoviedb.org/3/movie/${movieId}?api_key=${API_KEY}&language=en-US`);
      if (!res.ok) throw new Error('Failed');
      return await res.json();
    } catch { return null; }
  };
  const fetchSeriesDetails = async (seriesId) => {
    try {
      const res = await fetch(`https://api.themoviedb.org/3/tv/${seriesId}?api_key=${API_KEY}&language=en-US`);
      if (!res.ok) throw new Error('Failed');
      return await res.json();
    } catch { return null; }
  };

  // ---- Modal handlers ----
  const openContentModal = async (contentId, type) => {
    scrollPositionRef.current = window.scrollY;
    try {
      let details;
      if (type === 'movie') details = await fetchMovieDetails(contentId);
      else if (type === 'tv') details = await fetchSeriesDetails(contentId);
      if (details) {
        setSelectedContent({ ...details, media_type: type });
        setContentType(type);
        setIsModalOpen(true);
        const params = new URLSearchParams(searchParams);
        if (type === 'movie') { params.set('movie', contentId); params.delete('series'); }
        else { params.set('series', contentId); params.delete('movie'); }
        setSearchParams(params);
      }
    } catch (e) { console.error(e); }
  };

  const handleCardClick = (content) => openContentModal(content.id, content.media_type);

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedContent(null);
    setContentType(null);
    const params = new URLSearchParams(searchParams);
    params.delete('movie');
    params.delete('series');
    setSearchParams(params);
    if (scrollPositionRef.current > 0) {
      window.scrollTo({ top: scrollPositionRef.current, behavior: 'auto' });
    }
  };

  // ========== FIX: Re‑open modal when navigating back ==========
  useEffect(() => {
    const movieId = searchParams.get('movie');
    const seriesId = searchParams.get('series');
    if (movieId && !isNaN(parseInt(movieId))) {
      openContentModal(parseInt(movieId), 'movie');
    } else if (seriesId && !isNaN(parseInt(seriesId))) {
      openContentModal(parseInt(seriesId), 'tv');
    }
  }, []); // only on mount

  const getItemsPerView = () => {
    if (windowWidth < 769) return 2;
    if (windowWidth <= 1024) return 2;
    return 4;
  };

  // --- Provider carousel specific ---
  const providerItemsPerView = () => {
    if (windowWidth < 769) return 3; // mobile: 3 cards
    return 8; // desktop: 9 cards
  };

  const shiftProviderCarousel = (direction) => {
    const itemsPerView = providerItemsPerView();
    const total = providerData.length;
    const maxStart = Math.max(0, total - itemsPerView);
    let newIndex = providerIndex;
    if (direction === 'left') {
      newIndex = Math.max(0, providerIndex - itemsPerView);
    } else {
      newIndex = Math.min(maxStart, providerIndex + itemsPerView);
    }
    setProviderIndex(newIndex);
  };

  const shouldShowProviderLeft = () => providerIndex > 0;
  const shouldShowProviderRight = () => {
    const itemsPerView = providerItemsPerView();
    const total = providerData.length;
    const maxStart = Math.max(0, total - itemsPerView);
    return providerIndex < maxStart;
  };

  const shiftCarousel = (genreName, direction) => {
    const genre = genreData.find(g => g.name === genreName);
    if (!genre) return;
    const items = genre.movies || genre.series || [];
    const currentIndex = currentIndexes[genreName] || 0;
    const container = wrapperRefs.current[genreName];
    if (!container) return;
    const itemsPerView = getItemsPerView();
    const maxStart = Math.max(0, items.length - itemsPerView);
    if (direction === 'left') {
      const newIndex = Math.max(0, currentIndex - 2);
      setCurrentIndexes(prev => ({ ...prev, [genreName]: newIndex }));
      return;
    }
    if (currentIndex >= maxStart) {
      const tag = genre.tag;
      if (FETCHABLE_TAGS.has(tag)) handleLoadMore(tag);
      return;
    }
    const newIndex = Math.min(maxStart, currentIndex + 2);
    setCurrentIndexes(prev => ({ ...prev, [genreName]: newIndex }));
  };

  const shouldShowLeftArrow = (genreName) => (currentIndexes[genreName] || 0) > 0;
  const canFetchMore = (tag) => {
    if (!hasFetched[tag]) return true;
    const totalPages = totalPagesCache[tag];
    if (!totalPages) return false;
    const fetchedCount = fetchedPagesRef.current[tag]?.size || 0;
    return fetchedCount < totalPages;
  };

  const getRightArrowState = (genre) => {
    const items = genre.movies || genre.series || [];
    const currentIndex = currentIndexes[genre.name] || 0;
    const itemsPerView = getItemsPerView();
    const maxStart = Math.max(0, items.length - itemsPerView);
    const extras = extraMovies[genre.tag] || [];
    const isLoading = loadingGenres[genre.tag] || backdropLoading[genre.tag] || false;
    const canScroll = currentIndex < maxStart;
    const hasExtras = extras.length > 0;
    const canFetch = FETCHABLE_TAGS.has(genre.tag) && canFetchMore(genre.tag);
    const shouldShow = canScroll || hasExtras || canFetch || isLoading;
    let type = 'none';
    let disabled = false;
    if (isLoading) {
      type = 'spinner';
      disabled = true;
    } else if (canScroll) {
      type = 'arrow';
      disabled = false;
    } else if (hasExtras) {
      type = 'arrow';
      disabled = false;
    } else if (canFetch) {
      if (cooldownSeconds > 0) {
        type = 'countdown';
        disabled = true;
      } else {
        type = 'arrow';
        disabled = false;
      }
    }
    return { visible: shouldShow, disabled, type };
  };

  const allTags = [
    ...SUPPORTED_GENRES.filter(g => g.tag !== 'Documentary').map(g => g.tag),
    "TV Series",
    ...(specialOccasion ? [specialOccasion.tag] : []),
    "Documentary"
  ];

  const specialOccasionName = specialOccasion ? getTranslatedSpecialName(specialOccasion.name) : null;
  const specialOccasionTag = specialOccasion ? getTranslatedSpecialTag(specialOccasion.tag) : null;
  const promptQuestionEn = translations.en.languagePrompt.question;
  const promptQuestionFr = translations.fr.languagePrompt.question;
  const promptYes = getTranslation('languagePrompt.yes');
  const promptNo = getTranslation('languagePrompt.no');

  return (
    <div className="page-content active" id="recommendedPage">
      {initialLoading && (
        <div className="loading-overlay">
          <img src="/loading2.png" alt="Loading..." className="blinking-logo" />
        </div>
      )}

      {!initialLoading && (
        <>
          {showLanguagePrompt && (
            <div className="language-prompt-banner">
              <div className="language-prompt-content">
                <div className="language-prompt-text">
                  <span className="lang-fr">{promptQuestionFr}</span>
                  <span className="lang-en">{promptQuestionEn}</span>
                </div>
                <div className="language-prompt-actions">
                  <button className="lang-prompt-btn yes" onClick={handleLanguagePromptYes}>{promptYes}</button>
                  <button className="lang-prompt-btn no" onClick={handleLanguagePromptNo}>{promptNo}</button>
                </div>
              </div>
            </div>
          )}

          <h2 className="trending-title"></h2>
          
          {preferenceMessage && (
            <div className="preference-confirmation" onClick={() => setPreferenceMessage(null)} style={{cursor: 'pointer'}}>
              <div className="confirmation-content"><span>{preferenceMessage}</span></div>
            </div>
          )}
          
          {process.env.NODE_ENV === 'development' && (
            <div style={{position: 'fixed', bottom: '10px', right: '10px', zIndex: 1000, background: '#fff', padding: '10px', borderRadius: '5px', boxShadow: '0 2px 10px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column', gap: '10px'}}>
              <button onClick={resetToDefault} style={{background: '#2196F3', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '3px', cursor: 'pointer'}}>Reset to Default</button>
            </div>
          )}

          {/* ===== PROVIDER CAROUSEL (no title, with bottom padding) ===== */}
          <div className="genre-section provider-carousel" style={{ marginBottom: '30px' }}>
            <div className="carousel-container" style={{ position: 'relative' }}>
              {shouldShowProviderLeft() && (
                <button className="carousel-arrow left" onClick={() => shiftProviderCarousel('left')}>
                  <i className="fas fa-chevron-left"></i>
                </button>
              )}
              <div 
                className="movies-row-wrapper"
                ref={providerWrapperRef}
                style={{ overflow: 'hidden', width: '100%' }}
              >
                <div 
                  className="movies-row provider-row"
                  ref={providerRowRef}
                  style={{
                    display: 'flex',
                    gap: '16px',
                    transform: `translateX(-${providerIndex * (windowWidth < 769 ? (windowWidth / 3 - 16) : (windowWidth / 8 - 16))}px)`,
                    transition: 'transform 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)',
                    willChange: 'transform',
                    overflow: 'visible'
                  }}
                >
                  {providerData.map((provider) => {
                    const isMobile = windowWidth < 769;
                    return (
                      <div 
                        key={provider.id} 
                        className="provider-card"
                        onClick={() => navigate(`/movies?provider=${provider.id}`)}
                        style={{
                          flex: '0 0 auto',
                          width: isMobile ? 'calc(33.333% - 16px)' : 'calc(12.5% - 16px)',
                          cursor: 'pointer',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          position: 'relative',
                          aspectRatio: '16 / 9',
                          background: '#1a1a1a',
                          border: '1px solid rgba(255,255,255,0.1)',
                          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.03)'; e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.5)'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.3)'; }}
                      >
                        {/* Local PNG only, no fallback to fetched logo */}
                        <div
                          style={{
                            width: '100%',
                            height: '100%',
                            backgroundImage: `url(/providers/${provider.id}.png)`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat',
                          }}
                        />
                        {/* Show provider name only on desktop */}
                        {!isMobile && (
                          <div
                            style={{
                              position: 'absolute',
                              bottom: '0',
                              left: '0',
                              right: '0',
                              padding: '6px 8px',
                              background: 'rgba(0,0,0,0.6)',
                              color: 'rgba(255,255,255,0.9)',
                              fontSize: '12px',
                              fontWeight: '500',
                              textAlign: 'center',
                              backdropFilter: 'blur(4px)',
                            }}
                          >
                            {provider.name}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
              {shouldShowProviderRight() && (
                <button className="carousel-arrow right" onClick={() => shiftProviderCarousel('right')}>
                  <i className="fas fa-chevron-right"></i>
                </button>
              )}
            </div>
          </div>

          <div className="recommended-grid" id="recommendedContainer">
            {genreData.map((genre) => {
              const isSpecial = genre.isSpecial || false;
              const translatedName = isSpecial ? specialOccasionName : getTranslatedGenreName(genre.name);
              const translatedTag = isSpecial ? specialOccasionTag : getTranslatedGenreTag(genre.tag);
              const showLeft = shouldShowLeftArrow(genre.name);
              const rightArrowState = getRightArrowState(genre);
              const isLoading = loadingGenres[genre.tag] || backdropLoading[genre.tag] || false;
              const currentCooldown = cooldownSeconds;

              const cardWidth = cardWidths[genre.name] || 0;
              const gap = 16;
              const totalWidth = cardWidth + gap;
              const currentIndex = currentIndexes[genre.name] || 0;
              const offset = -currentIndex * totalWidth;

              return (
                <div className="genre-section" key={genre.name}>
                  <div className="genre-header" style={{ position: 'relative' }}>
                    <h3 className="genre-title">{translatedName}</h3>
                    <span className="genre-tag">{translatedTag}</span>
                    {/* Developer info: total pages available for this tag */}
                    {process.env.NODE_ENV === 'development' && genre.tag && (
                      <span className="dev-page-info" style={{
                        position: 'absolute',
                        right: '0',
                        top: '-20%',
                        transform: 'translateY(-50%)',
                        fontSize: '10px',
                        color: 'rgba(255,255,255,0.3)',
                        fontFamily: 'monospace',
                      }}>
                        pages: {totalPagesCache[genre.tag] || '?'}
                      </span>
                    )}
                  </div>
                  <div className="carousel-container" style={{ position: 'relative' }}>
                    {showLeft && (
                      <button className="carousel-arrow left" onClick={() => shiftCarousel(genre.name, 'left')}>
                        <i className="fas fa-chevron-left"></i>
                      </button>
                    )}
                    <div 
                      className="movies-row-wrapper"
                      ref={el => { wrapperRefs.current[genre.name] = el; }}
                      style={{ overflow: 'hidden', width: '100%' }}
                    >
                      <div 
                        className="movies-row"
                        ref={el => { rowRefs.current[genre.name] = el; }}
                        style={{
                          display: 'flex',
                          gap: `${gap}px`,
                          transform: `translateX(${offset}px)`,
                          transition: 'transform 0.4s cubic-bezier(0.25, 0.8, 0.25, 1)',
                          willChange: 'transform',
                          overflow: 'visible'
                        }}
                        data-genre={genre.name.replace(/\s+/g, '-').toLowerCase()}
                      >
                        {genre.movies && genre.movies.map((item) => {
                          const isHardcoded = item.isHardcoded === true;
                          const cacheKey = `${item.media_type || 'movie'}-${item.id}`;
                          const backdropMeta = backdropMap[cacheKey];
                          let backdropUrl = null;
                          let isEnglish = false;
                          if (isHardcoded) {
                            backdropUrl = item.backdrop_path ? `https://image.tmdb.org/t/p/w780${item.backdrop_path}` : null;
                          } else {
                            if (item.english_backdrop_url) {
                              backdropUrl = item.english_backdrop_url;
                              isEnglish = true;
                            } else if (backdropMeta) {
                              backdropUrl = backdropMeta.url;
                              isEnglish = backdropMeta.isEnglish;
                            } else if (item.backdrop_path) {
                              backdropUrl = `https://image.tmdb.org/t/p/w780${item.backdrop_path}`;
                              isEnglish = false;
                            }
                          }
                          const showTitleOverlay = !isHardcoded && !isEnglish && backdropUrl;
                          const isLoadingCard = isHardcoded ? false : (isLoading && !backdropUrl);

                          let year = item.year || null;
                          let extraInfo = item.extraInfo || null;

                          if (!year || !extraInfo) {
                            const detailKey = `${item.media_type || 'movie'}-${item.id}`;
                            const details = detailsMap[detailKey];
                            if (details) {
                              if (item.media_type === 'movie') {
                                const releaseDate = details.release_date;
                                year = year || (releaseDate ? releaseDate.slice(0,4) : null);
                                const runtime = details.runtime;
                                if (runtime) {
                                  const hours = Math.floor(runtime / 60);
                                  const minutes = runtime % 60;
                                  extraInfo = extraInfo || (hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`);
                                }
                              } else if (item.media_type === 'tv') {
                                const firstAir = details.first_air_date;
                                const lastAir = details.last_air_date;
                                year = year || (firstAir ? firstAir.slice(0,4) : null);
                                let yearRange = '';
                                if (firstAir && lastAir) {
                                  const firstYear = firstAir.slice(0,4);
                                  const lastYear = lastAir.slice(0,4);
                                  yearRange = firstYear === lastYear ? firstYear : `${firstYear}-${lastYear}`;
                                } else if (firstAir) {
                                  yearRange = firstAir.slice(0,4);
                                } else {
                                  yearRange = 'N/A';
                                }
                                const seasons = details.number_of_seasons;
                                let seasonText = '';
                                if (seasons) {
                                  seasonText = seasons === 1 ? '1 Season' : `${seasons} Seasons`;
                                } else {
                                  seasonText = 'N/A';
                                }
                                extraInfo = extraInfo || `${yearRange} • ${seasonText}`;
                              }
                            }
                          }

                          return (
                            <RecommendedCard
                              key={`${genre.name}-${item.id}`}
                              content={item}
                              isMobile={windowWidth < 769}
                              onClick={handleCardClick}
                              backdropLogoUrl={backdropUrl}
                              isLoading={isLoadingCard}
                              showTitleOverlay={showTitleOverlay}
                              year={year}
                              extraInfo={extraInfo}
                              ratingFormat={ratingFormat}
                            />
                          );
                        })}
                        {genre.series && genre.series.map((item) => {
                          const isHardcoded = item.isHardcoded === true;
                          const cacheKey = `${item.media_type || 'tv'}-${item.id}`;
                          const backdropMeta = backdropMap[cacheKey];
                          let backdropUrl = null;
                          let isEnglish = false;
                          if (isHardcoded) {
                            backdropUrl = item.backdrop_path ? `https://image.tmdb.org/t/p/w780${item.backdrop_path}` : null;
                          } else {
                            if (item.english_backdrop_url) {
                              backdropUrl = item.english_backdrop_url;
                              isEnglish = true;
                            } else if (backdropMeta) {
                              backdropUrl = backdropMeta.url;
                              isEnglish = backdropMeta.isEnglish;
                            } else if (item.backdrop_path) {
                              backdropUrl = `https://image.tmdb.org/t/p/w780${item.backdrop_path}`;
                              isEnglish = false;
                            }
                          }
                          const showTitleOverlay = !isHardcoded && !isEnglish && backdropUrl;
                          const isLoadingCard = isHardcoded ? false : (isLoading && !backdropUrl);

                          let year = item.year || null;
                          let extraInfo = item.extraInfo || null;
                          if (!year || !extraInfo) {
                            const detailKey = `${item.media_type || 'tv'}-${item.id}`;
                            const details = detailsMap[detailKey];
                            if (details) {
                              const firstAir = details.first_air_date;
                              const lastAir = details.last_air_date;
                              year = year || (firstAir ? firstAir.slice(0,4) : null);
                              let yearRange = '';
                              if (firstAir && lastAir) {
                                const firstYear = firstAir.slice(0,4);
                                const lastYear = lastAir.slice(0,4);
                                yearRange = firstYear === lastYear ? firstYear : `${firstYear}-${lastYear}`;
                              } else if (firstAir) {
                                yearRange = firstAir.slice(0,4);
                              } else {
                                yearRange = 'N/A';
                              }
                              const seasons = details.number_of_seasons;
                              let seasonText = '';
                              if (seasons) {
                                seasonText = seasons === 1 ? '1 Season' : `${seasons} Seasons`;
                              } else {
                                seasonText = 'N/A';
                              }
                              extraInfo = extraInfo || `${yearRange} • ${seasonText}`;
                            }
                          }

                          return (
                            <RecommendedCard
                              key={`${genre.name}-${item.id}`}
                              content={item}
                              isMobile={windowWidth < 769}
                              onClick={handleCardClick}
                              backdropLogoUrl={backdropUrl}
                              isLoading={isLoadingCard}
                              showTitleOverlay={showTitleOverlay}
                              year={year}
                              extraInfo={extraInfo}
                              ratingFormat={ratingFormat}
                            />
                          );
                        })}
                      </div>
                    </div>
                    {rightArrowState.visible && (
                      <button 
                        className={`carousel-arrow right ${rightArrowState.disabled ? 'disabled' : ''}`}
                        onClick={() => {
                          if (rightArrowState.disabled) return;
                          shiftCarousel(genre.name, 'right');
                        }}
                        disabled={rightArrowState.disabled}
                      >
                        {rightArrowState.type === 'spinner' ? (
                          <i className="fas fa-spinner fa-spin" style={{ fontSize: windowWidth < 769 ? '18px' : undefined }}></i>
                        ) : rightArrowState.type === 'countdown' ? (
                          <span style={{fontSize: '14px', fontWeight: 'bold'}}>{currentCooldown}</span>
                        ) : (
                          <i className="fas fa-chevron-right"></i>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {!showGenreFilter ? (
            <div style={{display: 'flex', justifyContent: 'center', marginTop: '40px'}}>
              <button onClick={() => setShowGenreFilter(true)} className="preferences-toggle-btn">
                {getTranslation('ui.preferencesToggle')}
              </button>
            </div>
          ) : (
            <div className="preferences-container" ref={preferencesRef}>
              <h3 className="preferences-title">{getTranslation('ui.contentPreferences')}</h3>
              <p className="preferences-subtitle">{getTranslation('ui.chooseTypes')}</p>
              <div className="tags-grid">
                {allTags.map(tag => {
                  const isSpecialTag = specialOccasion && tag === specialOccasion.tag;
                  const displayTag = isSpecialTag ? specialOccasionTag : getTranslatedGenreTag(tag);
                  return (
                    <div key={tag} 
                         className={`tag-item ${selectedTags.includes(tag) ? 'selected' : ''} ${isSpecialTag ? 'special-tag' : ''}`}
                         onClick={() => toggleTag(tag)}
                         style={isSpecialTag && specialOccasion.buttonColor ? { '--special-color': specialOccasion.buttonColor } : {}}
                    >
                      <div className="tag-checkbox">{selectedTags.includes(tag) && <i className="fas fa-check"></i>}</div>
                      <span className="tag-label">{displayTag}</span>
                    </div>
                  );
                })}
              </div>

              <div className={`language-selection ${blinkLanguage ? 'blink-language' : ''}`}>
                <span className="language-label">{getTranslation('ui.defaultLanguage')}</span>
                <div className="language-buttons">
                  {['en', 'fr', 'es', 'it', 'nl'].map(lang => (
                    <button key={lang} className={`lang-btn ${selectedLanguage === lang ? 'selected' : ''}`} onClick={() => setSelectedLanguage(lang)}>
                      {lang.toUpperCase()}
                    </button>
                  ))}
                </div>
              </div>

              <div className="rating-format-selection">
                <span className="rating-format-label">{getTranslation('ui.ratingFormat')}</span>
                <div className="rating-format-buttons">
                  <button 
                    className={`rating-format-btn ${ratingFormat === 'stars' ? 'selected' : ''}`}
                    onClick={() => setRatingFormat('stars')}
                  >
                    <span className="format-preview">★★★★☆</span>
                  </button>
                  <button 
                    className={`rating-format-btn ${ratingFormat === 'number' ? 'selected' : ''}`}
                    onClick={() => setRatingFormat('number')}
                  >
                    <span className="format-preview">★ 9.7</span>
                  </button>
                </div>
              </div>

              <div className="preferences-actions">
                <button onClick={applyGenreFilter} className="apply-preferences-btn">{getTranslation('ui.applyPreferences')}</button>
              </div>
            </div>
          )}

          {isModalOpen && contentType === 'movie' && selectedContent && (
            <MoviesModal movie={selectedContent} onClose={closeModal} onUpdate={() => {}} />
          )}
          {isModalOpen && contentType === 'tv' && selectedContent && (
            <SeriesModal isOpen={isModalOpen} onClose={closeModal} seriesId={selectedContent.id} onUpdate={() => {}} />
          )}

          <style jsx>{`
            /* Prevent horizontal scroll */
            :global(html), :global(body) {
              overflow-x: hidden;
            }
            :global(.page-content) {
              overflow-x: hidden;
            }

            /* Fixed card widths */
            @media (min-width: 1025px) {
              .recommended-card { width: calc(25% - 20px) !important; }
            }
            @media (min-width: 768px) and (max-width: 1024px) {
              .recommended-card { width: calc(50% - 8px) !important; }
            }
            @media (max-width: 768px) {
              .recommended-card { width: calc(50% - 20px) !important; }
            }

            /* Provider carousel */
            .provider-carousel .carousel-container {
              margin-bottom: 0;
            }
            .provider-card {
              aspect-ratio: 16 / 9;
            }

            /* Lower the provider carousel arrows slightly */
            .provider-carousel .carousel-arrow {
              top: 50%;
            }

            .genre-header { display: flex; align-items: center; margin-bottom: 15px; }
            .genre-tag { background: rgba(124,252,0,0.2); color: #32CD32; padding: 4px 10px; border-radius: 20px; font-size: 12px; margin-left: 15px; font-weight: 600; }
            .preferences-container { display: flex; flex-direction: column; margin: 30px auto; background: rgba(20, 20, 20, 0.95); padding: 25px; border-radius: 12px; border: 1px solid rgba(255, 255, 255, 0.12); box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4); width: 100%; max-width: 2200px; backdrop-filter: blur(10px); }
            .preferences-title { color: #fff; font-size: 22px; font-weight: 600; margin-bottom: 8px; }
            .preferences-subtitle { color: rgba(255, 255, 255, 0.7); font-size: 14px; margin-bottom: 25px; }
            .tags-grid { display: flex; flex-wrap: wrap; justify-content: center; gap: 12px; margin-bottom: 25px; }
            .tag-item { display: flex; align-items: center; padding: 12px 20px; background: rgba(255, 255, 255, 0.08); border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 8px; cursor: pointer; transition: all 0.2s ease; min-width: 120px; justify-content: center; }
            .tag-item:hover { background: rgba(255, 255, 255, 0.12); border-color: rgba(255, 255, 255, 0.15); }
            .tag-item.selected { background: rgba(124,252,0,0.25); border-color: rgba(124,252,0,0.35); }
            .tag-item.special-tag { border-color: color-mix(in srgb, var(--special-color, #7CFC00) 50%, transparent); }
            .tag-item.special-tag.selected { background: color-mix(in srgb, var(--special-color, #7CFC00) 15%, transparent); border-color: color-mix(in srgb, var(--special-color, #7CFC00) 30%, transparent); }
            .tag-checkbox { width: 18px; height: 18px; border: 2px solid rgba(255, 255, 255, 0.3); border-radius: 4px; margin-right: 12px; display: flex; align-items: center; justify-content: center; transition: all 0.2s ease; }
            .tag-item.selected .tag-checkbox { background: rgba(124,252,0,0); border-color: #7CFC00; }
            .tag-item.special-tag.selected .tag-checkbox { background: var(--special-color, #7CFC00); border-color: var(--special-color, #7CFC00); }
            .tag-checkbox i { color: white; font-size: 10px; }
            .tag-label { color: #fff; font-size: 14px; font-weight: 500; }
            .language-selection { display: flex; align-items: center; gap: 15px; justify-content: center; margin-bottom: 25px; flex-wrap: wrap; }
            .language-label { color: rgba(255, 255, 255, 0.8); font-size: 14px; font-weight: 500; }
            .language-buttons { display: flex; gap: 8px; }
            .lang-btn { padding: 6px 14px; background: transparent; border: 1px solid rgba(255, 255, 255, 0.2); border-radius: 4px; color: rgba(255, 255, 255, 0.7); font-size: 12px; font-weight: 500; cursor: pointer; transition: all 0.2s ease; text-transform: uppercase; letter-spacing: 0.5px; }
            .lang-btn:hover { background: rgba(255, 255, 255, 0.05); border-color: rgba(255, 255, 255, 0.3); }
            .lang-btn.selected { border-color: #7CFC00; color: #fff; background: rgba(124,252,0,0.15); }
            .preferences-actions { display: flex; justify-content: center; }
            .apply-preferences-btn { padding: 12px 40px; background: linear-gradient(45deg, #37ff14ab, #7efc00a6); border: 0px solid rgba(255, 255, 255, 0.15); color: white; border-radius: 6px; font-size: 14px; font-weight: 600; cursor: pointer; transition: all 0.2s ease; }
            .apply-preferences-btn:hover { background: linear-gradient(45deg, #37ff14cb, #7efc00a6); transform: translateY(-2px); }
            .preferences-toggle-btn { margin-bottom: 25px; padding: 12px 30px; background: rgba(255, 255, 255, 0.1); border: 1px solid rgba(255, 255, 255, 0.15); border-radius: 6px; color: white; font-size: 14px; font-weight: 500; cursor: pointer; transition: all 0.3s ease; }
            .preferences-toggle-btn:hover { background: rgba(255, 255, 255, 0.15); }
            .preference-confirmation { position: fixed; top: 120px; left: 50%; transform: translateX(-50%); z-index: 1000; background: rgba(20, 20, 20, 0.29); border: 1px solid rgba(255, 255, 255, 0.12); border-radius: 8px; padding: 15px 20px; box-shadow: 0 5px 20px rgba(0, 0, 0, 0.3); backdrop-filter: blur(10px); animation: slideInDown 0.3s ease; }
            .confirmation-content { display: flex; align-items: center; gap: 15px; }
            .confirmation-content span { color: #fff; font-size: 14px; }
            .close-message { background: none; border: none; color: rgba(255, 255, 255, 0.7); cursor: pointer; padding: 5px; border-radius: 4px; transition: all 0.2s ease; }
            .close-message:hover { background: rgba(255, 255, 255, 0.1); color: #fff; }
            @keyframes slideInDown { from { opacity: 0; transform: translate(-50%, -20px); } to { opacity: 1; transform: translate(-50%, 0); } }
            @media (max-width: 768px) { .tags-grid { grid-template-columns: repeat(2, 1fr); } .tag-item { display: flex; align-items: center; padding: 12px 20px; background: rgba(255, 255, 255, 0.08); border: 1px solid rgba(255, 255, 255, 0.1); border-radius: 8px; cursor: pointer; transition: all 0.2s ease; flex: 1; justify-content: left; } .language-selection { flex-direction: column; gap: 10px; } .preference-confirmation { width: 90%; text-align: center; } }

            .language-prompt-banner { background: rgba(20, 20, 20, 0.38); border: 1px solid rgba(253, 253, 252, 0.25); border-radius: 12px; padding: 18px 24px; margin: 10px auto 20px auto; max-width: 1200px; width: 100%; box-shadow: 0 4px 20px rgba(0,0,0,0.5); backdrop-filter: blur(10px); box-sizing: border-box; }
            .language-prompt-content { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 15px; }
            .language-prompt-text { display: flex; flex-direction: column; gap: 2px; font-size: 18px; font-weight: 500; }
            .lang-fr { color: #ffffff; }
            .lang-en { color: rgba(255,255,255,0.8); }
            .language-prompt-actions { display: flex; gap: 12px; }
            .lang-prompt-btn { padding: 8px 24px; border: none; border-radius: 30px; font-size: 16px; font-weight: 600; cursor: pointer; transition: all 0.2s ease; }
            .lang-prompt-btn.yes { background: #7CFC00; color: #111; }
            .lang-prompt-btn.yes:hover { background: #6ae000; transform: scale(1.05); }
            .lang-prompt-btn.no { background: rgba(255, 255, 255, 0.06); color: #fff; border: 1px solid rgba(255,255,255,0.3); }
            .lang-prompt-btn.no:hover { background: rgba(255,255,255,0.25); transform: scale(1.05); }
            .blink-language { animation: blinkBorderGreen 0.8s ease-in-out 3; }
            @keyframes blinkBorderGreen { 0%,100% { border-color: transparent; box-shadow: none; } 50% { border-color: #7CFC00; box-shadow: 0 0 20px rgba(124,252,0,0.6); } }
            @media (max-width: 768px) { .language-prompt-content { flex-direction: column; align-items: stretch; text-align: center; } .language-prompt-actions { justify-content: center; } .lang-fr, .lang-en { font-size: 14px; } .lang-prompt-btn { padding: 6px 18px; font-size: 14px; } }

            /* Rating format styles */
            .rating-format-selection {
              display: flex;
              align-items: center;
              gap: 15px;
              justify-content: center;
              margin-bottom: 25px;
              flex-wrap: wrap;
            }
            .rating-format-label {
              color: rgba(255, 255, 255, 0.8);
              font-size: 14px;
              font-weight: 500;
            }
            .rating-format-buttons {
              display: flex;
              gap: 10px;
            }
            .rating-format-btn {
              padding: 6px 18px;
              background: transparent;
              border: 1px solid rgba(255, 255, 255, 0.2);
              border-radius: 4px;
              color: rgba(255, 255, 255, 0.7);
              font-size: 14px;
              font-weight: 500;
              cursor: pointer;
              transition: all 0.2s ease;
            }
            .rating-format-btn:hover {
              background: rgba(255, 255, 255, 0.05);
              border-color: rgba(255, 255, 255, 0.3);
            }
            .rating-format-btn.selected {
              border-color: #7CFC00;
              color: #fff;
              background: rgba(124,252,0,0.15);
            }
            .format-preview {
              font-size: 0.8rem;
              letter-spacing: 1px;
            }
            .preferences-container .rating-format-selection {
              margin-bottom: 20px;
            }
            @media (max-width: 768px) {
              .rating-format-selection {
                flex-direction: column;
                gap: 10px;
              }
            }

/* Force provider arrows 5px closer to center */
.provider-carousel .carousel-container .carousel-arrow.left {
  left: -22px !important;   /* was probably 10px → shift right by 5px */
}

.provider-carousel .carousel-container .carousel-arrow.right {
  right: -22px !important;  /* was probably 10px → shift left by 5px */
}

 @media (max-width: 1200px) {

/* Force provider arrows 5px closer to center */
.provider-carousel .carousel-container .carousel-arrow.left {
  left: -12px !important;   /* was probably 10px → shift right by 5px */
}
.provider-carousel .carousel-container .carousel-arrow.right {
  right: -12px !important;  /* was probably 10px → shift left by 5px */
          }}
            
          `}</style>
        </>
      )}
    </div>
  );
};

export default RecommendedPage;